import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { assignmentTableServiceOptimized } from '../services/assignmentTableServiceOptimized';
import {
  assignmentTableServiceOptimizedV4,
  AssignmentTableData,
  AssignmentInfo,
  AssignmentStatusDetailByAssignment
} from '../services/assignmentTableServiceOptimizedV4';
import { assignmentCategoryService } from '../services/assignmentCategoryService';
import { firebaseReadCounter } from '../services/firebaseReadCounter';
import { projectAssignmentService } from '../services/projectAssignmentService';
import { assignmentService } from '../services/assignmentService';
import { routineAssignmentService } from '../services/routineAssignmentService';
import { assignmentMemoService } from '../services/assignmentMemoService';
import { userDataService } from '../services/userDataService';
import { AssignmentCategory } from '../types/alarm';
import StudentDetailModal from '../components/StudentDetailModal';
import { isStandardPlanOrAbove } from '../utils/licenseLimits';
import AssignmentCheckModal from '../components/AssignmentCheckModal';
import ConfirmModal from '../components/ConfirmModal';

type PendingCellData = { status: string; points: number; memo: string };

/** cellKey → 멤버·과제키 분리 (studentId에 하이픈이 있어도 긴 id 우선 매칭) */
function resolveCellKeyToStudentAndAssignment(
  cellKey: string,
  tableData: AssignmentTableData
): { studentId: string; assignmentKey: string } | null {
  const sorted = [...tableData.studentStatuses].sort((a, b) => b.studentId.length - a.studentId.length);
  for (const s of sorted) {
    const prefix = `${s.studentId}-`;
    if (cellKey.startsWith(prefix)) {
      return { studentId: s.studentId, assignmentKey: cellKey.slice(prefix.length) };
    }
  }
  return null;
}

/**
 * 테이블에 로드된 스냅샷과 동일하면 "미저장 변경 없음"으로 본다.
 * (스냅샷에 메모가 없으므로 메모가 비어 있지 않으면 항상 변경 있음으로 처리)
 */
function isPendingChangeEquivalentToTable(
  cellKey: string,
  pending: PendingCellData,
  tableData: AssignmentTableData
): boolean {
  const resolved = resolveCellKeyToStudentAndAssignment(cellKey, tableData);
  if (!resolved) return false;
  const student = tableData.studentStatuses.find(st => st.studentId === resolved.studentId);
  if (!student) return false;
  const baseline = student.assignmentStatuses[resolved.assignmentKey];
  if (!baseline) return false;
  if (pending.status !== baseline.status) return false;
  if (pending.memo && pending.memo.trim() !== '') return false;
  return true;
}

function prunePendingChangesAgainstTable(
  prev: Map<string, PendingCellData>,
  tableData: AssignmentTableData
): Map<string, PendingCellData> {
  let removed = false;
  const next = new Map(prev);
  Array.from(prev.entries()).forEach(([k, v]) => {
    if (isPendingChangeEquivalentToTable(k, v, tableData)) {
      next.delete(k);
      removed = true;
    }
  });
  return removed ? next : prev;
}

type LoadedStudentDetail = {
  status: string;
  points: number;
  memo: string;
  submissionLink: string;
  submissionMessage: string;
  submittedAt?: Date;
  submissionImagePath?: string;
  submissionImageUpdatedAt?: string;
  isDeleted: boolean;
};

function getAssignmentStatusKey(
  assignment: AssignmentInfo,
  assignmentType: 'assignment' | 'project' | 'routine'
): string {
  if (assignmentType === 'project' && assignment.subTaskId) {
    return `${assignment.id}-${assignment.subTaskId}`;
  }
  if (assignmentType === 'routine' && assignment.routineAssignmentId && assignment.dateId) {
    return `${assignment.routineAssignmentId}-${assignment.dateId}`;
  }
  return assignment.id;
}

function buildDetailCacheKey(
  studentId: string,
  assignment: AssignmentInfo,
  assignmentType: 'assignment' | 'project' | 'routine'
): string {
  return `${studentId}-${getAssignmentStatusKey(assignment, assignmentType)}`;
}

function mapLoadedStudentDetail(studentStatus: any): LoadedStudentDetail {
  const submittedAtRaw = studentStatus?.submittedAt;
  const submittedAt =
    submittedAtRaw instanceof Date
      ? submittedAtRaw
      : submittedAtRaw
        ? new Date(submittedAtRaw)
        : undefined;

  return {
    status: studentStatus?.status || 'pending',
    points: studentStatus?.earnedPoints ?? studentStatus?.points ?? 0,
    memo: studentStatus?.memo || '',
    submissionLink: studentStatus?.submissionLink || '',
    submissionMessage: studentStatus?.submissionMessage || '',
    submittedAt,
    submissionImagePath: studentStatus?.submissionImagePath,
    submissionImageUpdatedAt: studentStatus?.submissionImageUpdatedAt,
    isDeleted: false
  };
}

async function loadStudentDetailFromServer(
  userId: string,
  classId: string,
  studentId: string,
  assignment: AssignmentInfo,
  assignmentType: 'assignment' | 'project' | 'routine',
  memberGroup?: string
): Promise<LoadedStudentDetail> {
  const empty: LoadedStudentDetail = {
    status: 'pending',
    points: 0,
    memo: '',
    submissionLink: '',
    submissionMessage: '',
    isDeleted: false
  };

  try {
    if (assignmentType === 'assignment') {
      const result = await assignmentService.loadAssignmentWithStatuses(userId, classId, assignment.id);
      const studentStatus = result.statuses?.find((s: any) => s.studentId === studentId);
      return studentStatus ? mapLoadedStudentDetail(studentStatus) : empty;
    }

    if (assignmentType === 'project' && assignment.subTaskId) {
      const projectId = assignment.id;
      const result = await projectAssignmentService.loadProjectAssignmentStudentStatuses(
        userId,
        classId,
        projectId,
        assignment.subTaskId,
        memberGroup
      );
      const studentStatus = result.find((s) => s.studentId === studentId);
      return studentStatus ? mapLoadedStudentDetail(studentStatus) : empty;
    }

    if (assignmentType === 'routine') {
      const routineId = assignment.routineAssignmentId || assignment.id;
      const dateId = assignment.dateId;
      if (!dateId) return empty;

      const result = await routineAssignmentService.loadRoutineAssignmentWithStatuses(
        userId,
        classId,
        routineId,
        memberGroup
      );
      const dateStatus = result.dateStatuses?.find((d) => d.dateId === dateId);
      const studentStatus = dateStatus?.students?.[studentId];
      return studentStatus ? mapLoadedStudentDetail(studentStatus) : empty;
    }
  } catch (error) {
    console.error('❌ 멤버 상세 데이터 서버 로드 실패:', error);
  }

  return empty;
}

function mergeStudentDetailWithCaches(
  loaded: LoadedStudentDetail,
  cacheKey: string,
  pendingChanges: Map<string, PendingCellData>,
  submissionChanges: Map<string, { link: string; message: string; isDeleted: boolean }>,
  tableData: AssignmentTableData
): LoadedStudentDetail {
  const merged = { ...loaded };
  const cachedData = pendingChanges.get(cacheKey);
  if (cachedData && !isPendingChangeEquivalentToTable(cacheKey, cachedData, tableData)) {
    merged.status = cachedData.status;
    merged.points = cachedData.points;
    merged.memo = cachedData.memo;
  }
  const cachedSubmission = submissionChanges.get(cacheKey);
  if (cachedSubmission) {
    merged.submissionLink = cachedSubmission.link;
    merged.submissionMessage = cachedSubmission.message;
    merged.isDeleted = cachedSubmission.isDeleted;
  }
  return merged;
}

type StatusDetailCache = Map<string, Map<string, any>>;

function mergeStatusDetailByAssignmentIntoCache(
  target: StatusDetailCache,
  source?: AssignmentStatusDetailByAssignment
) {
  if (!source) return;
  Object.entries(source).forEach(([assignmentKey, byStudent]) => {
    if (!target.has(assignmentKey)) {
      target.set(assignmentKey, new Map());
    }
    const map = target.get(assignmentKey)!;
    Object.entries(byStudent).forEach(([studentId, raw]) => {
      map.set(studentId, raw);
    });
  });
}

function ingestStatusesIntoCache(target: StatusDetailCache, assignmentKey: string, statuses: any[]) {
  if (!target.has(assignmentKey)) {
    target.set(assignmentKey, new Map());
  }
  const map = target.get(assignmentKey)!;
  statuses.forEach((s) => {
    if (s?.studentId) {
      map.set(s.studentId, s);
    }
  });
}

function getDetailFromStatusCache(
  cache: StatusDetailCache,
  assignmentKey: string,
  studentId: string
): LoadedStudentDetail | null {
  const raw = cache.get(assignmentKey)?.get(studentId);
  return raw ? mapLoadedStudentDetail(raw) : null;
}

/** assignmentMemo-1 문서 1회 읽기(세션 캐시)로 모든 과제 메모를 statusDetail 캐시에 병합 */
async function enrichStatusDetailCacheWithMemos(
  cache: StatusDetailCache,
  statusDetailByAssignment: AssignmentStatusDetailByAssignment | undefined,
  userId: string,
  classId: string,
  memberGroup?: string
): Promise<void> {
  if (!statusDetailByAssignment) return;

  try {
    const memoDoc = await assignmentMemoService.loadMemoDocument(userId, classId, memberGroup);

    Object.entries(statusDetailByAssignment).forEach(([assignmentKey, byStudent]) => {
      const memoMap = memoDoc.assignments?.[assignmentKey] || {};
      if (!cache.has(assignmentKey)) {
        cache.set(assignmentKey, new Map());
      }
      const map = cache.get(assignmentKey)!;

      Object.entries(byStudent).forEach(([studentId, raw]) => {
        const memoFromDoc = memoMap[studentId]?.memo;
        const memo =
          memoFromDoc !== undefined && memoFromDoc !== ''
            ? memoFromDoc
            : raw.memo || '';
        map.set(studentId, { ...raw, memo });
      });
    });
  } catch (error) {
    console.error('❌ 메모 캐시 병합 실패:', error);
  }
}

function loadedDetailToRawStatus(studentId: string, detail: LoadedStudentDetail) {
  return {
    studentId,
    status: detail.status,
    points: detail.points,
    earnedPoints: detail.points,
    memo: detail.memo,
    submissionLink: detail.submissionLink,
    submissionMessage: detail.submissionMessage,
    submittedAt: detail.submittedAt,
    submissionImagePath: detail.submissionImagePath,
    submissionImageUpdatedAt: detail.submissionImageUpdatedAt
  };
}

function applyLoadedStudentDetailToSetter(
  detail: LoadedStudentDetail,
  setters: {
    setCurrentStatus: (v: string) => void;
    setCurrentPoints: (v: number) => void;
    setCurrentMemo: (v: string) => void;
    setCurrentSubmissionLink: (v: string) => void;
    setCurrentSubmissionMessage: (v: string) => void;
    setCurrentSubmittedAt: (v: Date | undefined) => void;
    setCurrentSubmissionImagePath: (v: string | undefined) => void;
    setCurrentSubmissionImageUpdatedAt: (v: string | undefined) => void;
    setCurrentIsDeleted: (v: boolean) => void;
  }
) {
  setters.setCurrentStatus(detail.status);
  setters.setCurrentPoints(detail.points);
  setters.setCurrentMemo(detail.memo);
  setters.setCurrentSubmissionLink(detail.submissionLink);
  setters.setCurrentSubmissionMessage(detail.submissionMessage);
  setters.setCurrentSubmittedAt(detail.submittedAt);
  setters.setCurrentSubmissionImagePath(detail.submissionImagePath);
  setters.setCurrentSubmissionImageUpdatedAt(detail.submissionImageUpdatedAt);
  setters.setCurrentIsDeleted(detail.isDeleted);
}

// 기존 스타일 컴포넌트들 (AssignmentTablePage.tsx에서 복사)
const Container = styled.div<{ $embeddedInModal?: boolean }>`
  min-height: ${props => (props.$embeddedInModal ? '0' : '100vh')};
  ${props =>
    props.$embeddedInModal
      ? `
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
    height: 100%;
    position: relative;
  `
      : ''}
  background: var(--color-bg);
  padding: ${props =>
    props.$embeddedInModal ? '0' : '36px 0 32px 0'}; /* 전체 페이지: 상단 헤더, 하단 네비바 공간 */
  margin: 0;
`;

const Header = styled.div<{ $embeddedInModal?: boolean }>`
  background: ${props => (props.$embeddedInModal ? 'var(--color-bg)' : 'var(--color-accent-strong)')};
  color: ${props => (props.$embeddedInModal ? 'var(--color-text-primary)' : 'white')};
  padding: ${props => (props.$embeddedInModal ? '16px 20px' : '4px 16px')};
  display: flex;
  align-items: center;
  justify-content: ${props => (props.$embeddedInModal ? 'flex-start' : 'center')};
  position: ${props => (props.$embeddedInModal ? 'sticky' : 'fixed')};
  top: 0;
  left: ${props => (props.$embeddedInModal ? 'auto' : '0')};
  right: ${props => (props.$embeddedInModal ? 'auto' : '0')};
  width: ${props => (props.$embeddedInModal ? '100%' : 'auto')};
  z-index: ${props => (props.$embeddedInModal ? 1100 : 1000)};
  height: ${props => (props.$embeddedInModal ? 'auto' : '36px')};
  min-height: ${props => (props.$embeddedInModal ? '52px' : '36px')};
  border-bottom: ${props => (props.$embeddedInModal ? '1px solid var(--color-border)' : 'none')};
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: white;
  width: 28px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.7;
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const ModalCloseButton = styled.button`
  width: 36px;
  height: 36px;
  margin-left: auto;
  border: none;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  transition: all 0.2s ease;

  &:hover {
    background: #f5f5f5;
    color: #6c757d;
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const Title = styled.h1<{ $embeddedInModal?: boolean }>`
  margin: 0;
  font-size: ${props => (props.$embeddedInModal ? '18px' : '16px')};
  font-weight: ${props => (props.$embeddedInModal ? 700 : 600)};
  color: ${props => (props.$embeddedInModal ? 'var(--color-text-primary)' : 'white')};
  flex: ${props => (props.$embeddedInModal ? '1' : '1')};
  min-width: 0;
  text-align: ${props => (props.$embeddedInModal ? 'left' : 'center')};
`;

const Content = styled.div<{ $embeddedInModal?: boolean }>`
  padding-top: 20px;
  padding-left: 20px;
  padding-right: 20px;
  padding-bottom: 20px;
  ${props =>
    props.$embeddedInModal
      ? `
    height: calc(100% - 52px);
    overflow-y: hidden;
    overflow-x: hidden;
    display: flex;
    flex-direction: column;
    min-height: 0;
  `
      : ''}
`;

/** 모달: 필터 아래 남은 세로 공간을 채우고 안내 문구만 가운데 */
const ModalMessageFill = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 0;
  text-align: center;
`;

const TopSection = styled.div`
  position: relative;
  display: block;
  width: 100%;
  margin-bottom: 20px;
  flex-shrink: 0;

  @media (max-width: 900px) {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }
`;

const ToggleSection = styled.div`
  display: flex;
  align-items: center;
  width: fit-content;
  max-width: none;
  margin: 0;
  gap: 0;
  position: absolute;
  left: 50%;
  top: 0;
  transform: translateX(-50%);
  z-index: 1;

  @media (max-width: 900px) {
    position: static;
    left: auto;
    top: auto;
    transform: none;
    width: auto;
    justify-content: center;
  }
`;

const ZoomToggleButton = styled.button`
  background: none;
  border: none;
  padding: 2px;
  height: 26px;
  min-height: 26px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  box-sizing: border-box;
  
  &:hover {
    opacity: 0.7;
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
  
  img {
    width: 20px;
    height: 20px;
    filter: var(--zoom-icon-filter);
  }
`;

const FilterSection = styled.div`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  justify-content: space-between;
  width: 100%;
  max-width: none;
  margin: 0;
`;


const ToggleButtonSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  max-width: none;
  margin: 0;
  gap: 12px;
  flex-wrap: nowrap;
  
  @media (max-width: 480px) {
    gap: 8px;
  }
  
  @media (max-width: 600px) {
    flex-direction: column;
    align-items: center;
    gap: 8px;
  }
`;

const ActionButtonsContainer = styled.div<{ $isAllIncomplete?: boolean }>`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: center;
  margin-left: auto;
  
  @media (max-width: 600px) {
    width: 100%;
    justify-content: center;
  }
  
  @media (max-width: 480px) {
    gap: 8px;
  }
`;

const FilterButton = styled.button<{ $active: boolean }>`
  padding: 4px 16px;
  height: 26px;
  min-height: 26px;
  background: var(--color-surface);
  color: ${props => props.$active ? 'var(--color-text-primary)' : 'var(--color-text-muted)'};
  border: 1px solid var(--color-border);
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: ${props => props.$active ? '600' : '400'};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  white-space: nowrap;
  box-sizing: border-box;
  min-width: 100px;

  &:hover {
    background-color: var(--color-panel);
    border-color: var(--color-text-muted);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(71, 112, 246, 0.3);
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px rgba(71, 112, 246, 0.3);
  }
  
  @media (max-width: 480px) {
    padding: 4px 8px;
    min-width: 60px;
    font-size: 0.8rem;
  }
`;

const FilterButtonText = styled.span`
  @media (max-width: 480px) {
    display: none;
  }
`;

const FilterButtonTextShort = styled.span`
  display: none;
  
  @media (max-width: 480px) {
    display: inline;
  }
`;


const ToggleContainer = styled.div<{ $tabCount?: number }>`
  display: flex;
  background: var(--color-border);
  border-radius: 12px;
  padding: 4px;
  position: relative;
  width: 100%;
  max-width: ${props => props.$tabCount === 4 ? '528px' : '384px'};
  min-width: ${props => props.$tabCount === 4 ? '360px' : '240px'};
  min-height: 34px;
  max-height: 34px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    max-width: ${props => props.$tabCount === 4 ? '456px' : '336px'};
    min-width: ${props => props.$tabCount === 4 ? '312px' : '228px'};
  }

  @media (max-width: 480px) {
    max-width: ${props => props.$tabCount === 4 ? '384px' : '264px'};
    min-width: ${props => props.$tabCount === 4 ? '264px' : '192px'};
    min-height: 32px;
    max-height: 32px;
  }
`;


const ToggleSlider = styled.div<{ $position: number; $tabCount?: number }>`
  position: absolute;
  top: 4px;
  left: ${props => {
    const tabCount = props.$tabCount || 3;
    const basePosition = props.$position * (100/tabCount);
    const sliderWidth = 100/tabCount;
    const margin = (100/tabCount - sliderWidth) / 2;
    // 탭별 미세 보정: 첫 탭은 약간 오른쪽, 마지막 탭은 약간 왼쪽
    const adjustment =
      props.$position === 0 ? 0.8 :
      props.$position === tabCount - 1 ? -0.45 :
      0.15;
    return `${basePosition + margin + adjustment}%`;
  }};
  width: ${props => `calc(${100/(props.$tabCount || 3)}% - 4px)`};
  height: calc(100% - 8px);
  background: var(--color-surface);
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: left 0.3s ease;
  z-index: 1;
`;

const ToggleOption = styled.button<{ $active: boolean }>`
  position: relative;
  z-index: 2;
  background: transparent;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 10px;
  height: 26px;
  min-height: 26px;
  max-height: 26px;
  font-size: 13px;
  font-weight: 500;
  line-height: 1;
  color: ${props => props.$active ? 'var(--color-text-primary)' : 'var(--color-text-muted)'};
  cursor: pointer;
  border-radius: 8px;
  transition: color 0.2s ease;
  white-space: nowrap;
  text-align: center;
  flex: 1;
  min-width: 0;
  box-sizing: border-box;
  -webkit-appearance: none;
  appearance: none;
  -webkit-text-size-adjust: 100%;
  text-size-adjust: 100%;

  &:hover {
    color: var(--color-text-primary);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  @media (max-width: 480px) {
    padding: 0 8px;
    height: 24px;
    min-height: 24px;
    max-height: 24px;
    font-size: 11px;
  }
`;

// 축약된 텍스트를 위한 컴포넌트
const ToggleText = styled.span`
  @media (max-width: 420px) {
    display: none;
  }
`;

const ToggleTextShort = styled.span`
  display: none;
  
  @media (max-width: 420px) {
    display: inline;
  }
`;

const FilterSelectContainer = styled.div`
  position: relative;
  min-width: fit-content;
  width: auto;

  @media (max-width: 600px) {
    margin: 0 auto;
  }
`;

// 루틴과제관리 페이지 스타일의 필터 컴포넌트들
const FilterSelectButton = styled.div`
  width: auto;
  min-width: fit-content;
  padding: 4px 0px;
  height: 26px;
  min-height: 26px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: var(--color-text-secondary);
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: none;
  transition: all 0.2s ease;

  &:hover {
    background-color: var(--color-panel);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(71, 112, 246, 0.3);
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px rgba(71, 112, 246, 0.3);
  }
`;

const FilterOption = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  border: none;
  background: transparent;
  width: 100%;
  text-align: left;
  font-size: 14px;
  font-weight: inherit;
  color: var(--color-text-secondary);

  &:hover {
    background-color: var(--color-border);
  }

  &:focus {
    outline: none;
    background-color: var(--color-border);
  }
`;

const ColorDot = styled.div<{ $color: string; $isToday?: boolean }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${props => props.$isToday ? 'var(--color-border)' : props.$color}; // 오늘 날짜는 연한 회색
`;

const FilterDropdownOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  background: transparent;
`;


const FilterDropdown = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: var(--color-surface);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1001;
  display: ${props => props.$isOpen ? 'block' : 'none'};
  min-width: 150px;
  border: 1px solid var(--color-border);
  overflow: hidden;
`;

const FilterDivider = styled.div`
  height: 1px;
  background: var(--color-border);
  margin: 4px 0;
`;

const ProjectFilterDropdown = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: var(--color-surface);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1001;
  display: ${props => props.$isOpen ? 'block' : 'none'};
  min-width: 200px;
  max-height: 240px;
  border: 1px solid var(--color-border);
  overflow-y: auto;
`;


const TableContainer = styled.div<{ $embeddedInModal?: boolean }>`
  background: transparent;
  box-shadow: none;
  width: ${props => (props.$embeddedInModal ? '100%' : 'fit-content')};
  max-width: ${props => (props.$embeddedInModal ? '100%' : '600px')};
  margin: 0 auto;
  border-radius: 0;
  overflow: ${props => (props.$embeddedInModal ? 'visible' : 'hidden')};
  ${props =>
    props.$embeddedInModal
      ? `
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
  `
      : ''}
`;

// 고정된 헤더 컨테이너

// 통합 헤더 그리드
const UnifiedHeaderGrid = styled.div<{ $columnCount: number; $embeddedInModal?: boolean }>`
  display: grid;
  grid-template-columns: 40px 60px repeat(${props => props.$columnCount - 2}, 80px);
  grid-template-rows: 28px 96px;
  min-width: ${props => 100 + (props.$columnCount - 2) * 80}px;
  width: fit-content;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 0;
  position: sticky;
  top: 0;
  z-index: 10;
`;

// 통합 데이터 그리드
const UnifiedDataGrid = styled.div<{ $columnCount: number; $rowCount: number; $isZoomed: boolean }>`
  display: grid;
  grid-template-columns: 40px 60px repeat(${props => props.$columnCount - 2}, 80px);
  grid-template-rows: repeat(${props => props.$rowCount}, ${props => props.$isZoomed ? '40px' : '20px'});
  min-width: ${props => 100 + (props.$columnCount - 2) * 80}px;
  width: fit-content;
  background: var(--color-surface);
  position: relative;
  border: 1px solid var(--color-border);
  border-top: none;
  border-radius: 0;
`;

const ScrollableContent = styled.div<{ $embeddedInModal?: boolean }>`
  overflow-x: auto;
  overflow-y: ${props => (props.$embeddedInModal ? 'auto' : 'auto')};
  max-height: ${props => (props.$embeddedInModal ? 'none' : 'calc(100vh - 200px)')};
  width: ${props => (props.$embeddedInModal ? '100%' : 'auto')};
  ${props =>
    props.$embeddedInModal
      ? `
    flex: 1;
    min-height: 0;
  `
      : ''}
  position: relative;

  /* 스크롤은 유지하고 스크롤바만 숨김 (Chrome / Firefox / Safari) */
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
    width: 0;
    height: 0;
  }
`;

const TableGridWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: fit-content;
  margin: 0 auto;
`;



// 멤버 데이터 그리드

// 통합 CSS Grid 테이블 컴포넌트들 (기존 유지)

const GridHeaderCell = styled.div`
  padding: 4px 8px;
  text-align: center;
  font-weight: 600;
  color: var(--color-text-secondary);
  border-right: 1px solid var(--color-border);
  border-bottom: 1px solid var(--color-border);
  font-size: 12px;
  background: var(--color-border);
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:last-child {
    border-right: none;
  }
`;

const GridDataCell = styled.div<{ $isZoomed?: boolean }>`
  padding: 0;
  border-right: 1px solid var(--color-border);
  border-bottom: 1px solid var(--color-border);
  font-size: ${props => props.$isZoomed ? '14px' : '12px'};
  color: var(--color-text-secondary);
  text-align: center;
  white-space: nowrap;
  background: var(--color-surface);
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${props => props.$isZoomed ? '40px' : '20px'};
  
  &:last-child {
    border-right: none;
  }
`;

// CSS Grid 기반 헤더 컴포넌트들 (기존 유지)



const DueDateCell = styled.div<{ color?: string }>`
  padding: 8px 4px 2px 4px;
  text-align: center;
  font-weight: 400;
  color: #6c757d;
  border-right: 1px solid var(--color-border);
  border-bottom: 1px solid var(--color-border);
  font-size: 10px;
  background: var(--color-panel);
  height: 40px;
  line-height: 1.1;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  grid-row: 2;
  z-index: 2;
  
  /* 카테고리 색상 막대 */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 6px;
    background: ${props => props.color || '#a2a2a2'};
    z-index: 1;
  }
  
  &:last-child {
    border-right: none;
  }
`;



const NumberHeaderCell = styled.div`
  padding: 4px 6px;
  text-align: center;
  font-weight: 600;
  color: var(--color-text-secondary);
  border-right: 1px solid var(--color-border);
  border-bottom: 1px solid var(--color-border);
  font-size: 12px;
  background: var(--color-border);
  white-space: nowrap;
  height: 124px;
  display: flex;
  align-items: center;
  justify-content: center;
  grid-column: 1;
  grid-row: 1 / 3;
  position: sticky;
  left: 0;
  z-index: 10;
`;

const NameHeaderCell = styled.div`
  padding: 4px 6px;
  text-align: center;
  font-weight: 600;
  color: var(--color-text-secondary);
  border-right: 1px solid var(--color-border);
  border-bottom: 1px solid var(--color-border);
  font-size: 12px;
  background: var(--color-border);
  white-space: nowrap;
  height: 124px;
  display: flex;
  align-items: center;
  justify-content: center;
  grid-column: 2;
  grid-row: 1 / 3;
  position: sticky;
  left: 40px;
  z-index: 10;
`;

const StatusCell = styled.div<{ $status: string; $isAssigned?: boolean; $isZoomed?: boolean; $isSelected?: boolean; $hasPendingChange?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  font-size: ${props => props.$isZoomed ? '14px' : '12px'};
  background: ${props => {
    if (props.$hasPendingChange) {
      // 변경 예정인 상태의 색상 (원래 색상으로 표시)
      switch (props.$status) {
        case 'pending': return 'var(--status-pending)';
        case 'confirmed': return 'var(--status-confirmed)';
        case 'delayed': return 'var(--status-delayed)';
        case 'exempted': return 'var(--status-exempted)';
        case 'submitted': return '#000000';
        default: return 'var(--status-pending)';
      }
    }
    if (props.$isAssigned === false) {
      return 'var(--color-border)'; // 테두리와 같은 색상 (비활성화)
    }
    switch (props.$status) {
      case 'pending': return 'var(--status-pending)';
      case 'confirmed': return 'var(--status-confirmed)';
      case 'delayed': return 'var(--status-delayed)';
      case 'exempted': return 'var(--status-exempted)';
      case 'submitted': return '#000000';
      default: return 'var(--status-pending)';
    }
  }};
  color: ${props => {
    if (props.$isAssigned === false) {
      return 'var(--color-text-secondary)'; // 더 진한 회색 텍스트
    }
    return props.$status === 'exempted' ? '#000000' : '#FFFFFF';
  }};
  font-weight: 600;
  font-size: 16px;
  opacity: ${props => props.$isAssigned === false ? 0.6 : 1};
  cursor: ${props => props.$isAssigned === false ? 'not-allowed' : 'pointer'};
  border-radius: 4px;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 4px;
    background: ${props => props.$isSelected ? 'rgba(0, 0, 0, 0.35)' : 'transparent'};
    pointer-events: none;
    z-index: 2;
  }
  
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    transition: transform 0.2s ease, box-shadow 0.2s ease;
  }
  
  &:focus-visible {
    outline: 2px solid #000;
  }
  
  ${props => props.$isSelected && `
    box-shadow: 0 0 0 3px rgba(71, 115, 110, 0.2);
  `}
`;

const StatusIcon = styled.div<{ $status: string; $isAssigned?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: ${props => {
    if (props.$isAssigned === false) {
      return 'var(--color-text-secondary)'; // 더 진한 회색 텍스트
    }
    return props.$status === 'exempted' ? '#000000' : '#FFFFFF';
  }};
`;

const LoadingText = styled.div`
  text-align: center;
  padding: 40px;
  color: #6c757d;
  font-size: 16px;
`;

const ErrorText = styled.div`
  text-align: center;
  padding: 40px;
  color: #dc3545;
  font-size: 16px;
`;

const EmptyText = styled.div`
  text-align: center;
  padding: 40px;
  color: #6c757d;
  font-size: 16px;
`;

// 상태 변경 버튼들 (과제 체크 모달과 동일한 스타일)
const StatusChangeSection = styled.div`
  display: flex;
  gap: 8px;
  margin: 16px auto;
  justify-content: center;
  width: 100%;
  max-width: 600px;
  min-width: 300px;
  height: 32px;
  min-height: 32px;
  align-items: center;
  flex-shrink: 0;
  
  
  @media (max-width: 480px) {
    min-width: 200px;
  }
  
  @media (max-width: 420px) {
    height: 28px;
    min-height: 28px;
    gap: 4px;
  }
  
  @media (max-width: 480px) {
    gap: 6px;
    flex-wrap: nowrap;
    height: 28px;
    min-height: 28px;
  }
  
  @media (max-width: 380px) {
    gap: 2px;
    flex-wrap: nowrap;
  }
  
  @media (max-width: 350px) {
    gap: 1px;
    flex-wrap: nowrap;
  }
`;

const StatusChangeButton = styled.button<{ $isSelected: boolean; $disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 6px 12px;
  background: var(--color-surface);
  color: ${props => props.$isSelected ? 'var(--color-text-primary)' : 'var(--color-text-muted)'};
  border: 1px solid var(--color-border);
  border-radius: 8px;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  flex: 1;
  height: 32px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  &:hover {
    ${props => !props.$disabled && `
      background-color: var(--color-panel);
      border-color: var(--color-text-muted);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
    `}
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
  
  @media (max-width: 420px) {
    justify-content: center;
    padding: 4px;
    height: 28px;
    min-width: 24px;
  }
  
  @media (max-width: 380px) {
    padding: 2px;
    min-width: 20px;
  }
`;

const StatusChangeButtonColorDot = styled.div<{ $status: string }>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  font-weight: 700;
  font-size: 12px;
  color: white;
  text-shadow: 0 1px 2px rgba(0,0,0,0.3);
  flex-shrink: 0;
  
  background: ${props => {
    switch (props.$status) {
      case 'pending': return 'var(--status-pending)';
      case 'confirmed': return '#227971';
      case 'delayed': return 'var(--status-delayed)';
      case 'exempted': return 'var(--status-exempted)';
      default: return 'var(--status-pending)';
    }
  }};
  
  @media (max-width: 420px) {
    margin-right: 0;
    width: 20px;
    height: 20px;
    font-size: 12px;
  }
`;

const StatusChangeButtonText = styled.div<{ $isSelected: boolean }>`
  font-size: 14px;
  font-weight: ${props => props.$isSelected ? '600' : '400'};
  color: ${props => props.$isSelected ? 'var(--color-text-primary)' : 'var(--color-text-muted)'};
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 600px) {
    font-size: 12px;
  }

  @media (max-width: 480px) {
    font-size: 11px;
  }

  @media (max-width: 420px) {
    display: none;
  }

  @media (max-width: 380px) {
    display: none;
  }

  @media (max-width: 350px) {
    display: none;
  }
`;

const SaveButton = styled.button<{ disabled: boolean }>`
  padding: 4px 12px;
  height: 26px;
  min-height: 26px;
  width: calc((600px - 24px) / 4);
  max-width: 144px;
  min-width: calc((300px - 24px) / 4);
  background: ${props => props.disabled ? 'var(--color-border)' : 'var(--color-accent-strong)'};
  color: ${props => props.disabled ? 'var(--color-dark-gray-text)' : 'white'};
  border: none;
  border-radius: 8px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;
  opacity: ${props => props.disabled ? 0.6 : 1};
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  box-sizing: border-box;
  
  &:hover:not(:disabled) {
    background: var(--color-accent);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
  }
  
  @media (max-width: 600px) {
    width: calc((600px - 24px) / 4);
    max-width: 144px;
    min-width: calc((300px - 24px) / 4);
  }
`;

// 상태 아이콘 함수
const getStatusIcon = (status: string): string => {
  switch (status) {
    case 'pending': return 'X';
    case 'confirmed': return 'V';
    case 'delayed': return '-';
    case 'exempted': return '!';
    default: return 'X';
  }
};

interface AssignmentTablePageProps {
  userId: string;
  classId: string;
  onBack: () => void;
  user: any;
  currentClass: any;
  currentMemberGroup?: string;
  onHasChangesChange?: (hasChanges: boolean) => void;
  onSaveAndExit?: () => Promise<void>;
  onSaveRef?: (saveFn: () => Promise<void>) => void;
  /** App 모달 안에 넣을 때: 고정 헤더/여백을 모달 기준으로 맞춤 */
  embeddedInModal?: boolean;
}

const AssignmentTablePageFinal: React.FC<AssignmentTablePageProps> = ({
  userId,
  classId,
  onBack,
  user,
  currentClass,
  currentMemberGroup = 'A',
  onHasChangesChange,
  onSaveAndExit,
  onSaveRef,
  embeddedInModal = false
}) => {
  const [tableData, setTableData] = useState<AssignmentTableData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [defaultPoints, setDefaultPoints] = useState<number>(1);
  const [statusDefaultPoints, setStatusDefaultPoints] = useState<{
    pending: number;
    confirmed: number;
    delayed: number;
    exempted: number;
  } | undefined>(undefined);
  const [filterType, setFilterType] = useState<'all' | 'incomplete'>('all');
  // 기본값: 미완료 전체
  const [filterAssignmentType, setFilterAssignmentType] = useState<'allIncomplete' | 'assignment' | 'project' | 'routine'>('allIncomplete');
  const [filterCategoryId, setFilterCategoryId] = useState<string>('all');
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const [filterProjectId, setFilterProjectId] = useState<string>('all');
  const [isProjectDropdownOpen, setIsProjectDropdownOpen] = useState(false);
  const [categories, setCategories] = useState<AssignmentCategory[]>([]);
  const [, setProjects] = useState<any[]>([]);
  const [extractedProjects, setExtractedProjects] = useState<any[]>([]);
  const [isZoomed, setIsZoomed] = useState(false);
  
  // 루틴 과제 필터 상태
  const [selectedRoutineDate, setSelectedRoutineDate] = useState<Date>(new Date());
  const [isRoutineDateDropdownOpen, setIsRoutineDateDropdownOpen] = useState(false);
  const routineDateDropdownRef = useRef<HTMLDivElement>(null);

  // 셀 선택 및 상태 변경 상태
  const [selectedCells, setSelectedCells] = useState<Set<string>>(new Set()); // "studentId-assignmentId" 형식
  const [pendingChanges, setPendingChanges] = useState<Map<string, { status: string; points: number; memo: string }>>(new Map()); // 변경된 상태, 포인트, 메모
  const [submissionChanges, setSubmissionChanges] = useState<Map<string, { link: string; message: string; isDeleted: boolean }>>(new Map()); // 제출 확인 데이터 변경사항
  const [hasChanges, setHasChanges] = useState(false);
  const changeSelectedCellsStatusRef = useRef<(newStatus: string) => void>(() => {});
  /** 테이블·과제체크 모달에서 이미 로드한 멤버 상태 (상세 모달용, 서버 재조회 최소화) */
  const statusDetailCacheRef = useRef<StatusDetailCache>(new Map());
  const memoEnrichPromiseRef = useRef<Promise<void> | null>(null);
  
  // 경고 모달 상태
  const [showWarningModal, setShowWarningModal] = useState(false);
  
  // hasChanges 변경 시 부모에게 알림
  useEffect(() => {
    if (onHasChangesChange) {
      onHasChangesChange(hasChanges);
    }
  }, [hasChanges, onHasChangesChange]);

  /** 원래 테이블 상태로 되돌린 셀은 pending에서 제거 */
  useEffect(() => {
    if (!tableData) return;
    setPendingChanges(prev => prunePendingChangesAgainstTable(prev, tableData));
  }, [tableData, pendingChanges]);

  useEffect(() => {
    if (!user?.id || !currentClass?.id) return;
    assignmentMemoService.invalidateMemoCache(user.id, currentClass.id, currentMemberGroup);
    statusDetailCacheRef.current = new Map();
    memoEnrichPromiseRef.current = null;
  }, [user?.id, currentClass?.id, currentMemberGroup]);

  useEffect(() => {
    if (!tableData?.statusDetailByAssignment) {
      memoEnrichPromiseRef.current = null;
      return;
    }

    mergeStatusDetailByAssignmentIntoCache(
      statusDetailCacheRef.current,
      tableData.statusDetailByAssignment
    );

    if (!user?.id || !currentClass?.id) {
      memoEnrichPromiseRef.current = null;
      return;
    }

    memoEnrichPromiseRef.current = enrichStatusDetailCacheWithMemos(
      statusDetailCacheRef.current,
      tableData.statusDetailByAssignment,
      user.id,
      currentClass.id,
      currentMemberGroup
    );
  }, [tableData?.statusDetailByAssignment, user?.id, currentClass?.id, currentMemberGroup]);

  /** 실제로 서버 스냅샷과 다른지 여부로 hasChanges 동기화 (hasChanges만 true로 두고 맵은 안 비우는 버그 방지) */
  useEffect(() => {
    if (!tableData) {
      setHasChanges(submissionChanges.size > 0);
      return;
    }
    const pendingDirty = Array.from(pendingChanges.entries()).some(
      ([k, v]) => !isPendingChangeEquivalentToTable(k, v, tableData)
    );
    setHasChanges(pendingDirty || submissionChanges.size > 0);
  }, [tableData, pendingChanges, submissionChanges]);
  
  // 저장 함수를 부모에게 전달 (handleSave는 나중에 정의되므로 useRef 사용)
  const handleSaveRef = useRef<(() => Promise<void>) | null>(null);
  
  // 더블탭 감지를 위한 상태
  const [tapTimers, setTapTimers] = useState<Map<string, NodeJS.Timeout>>(new Map());
  
  // 선택영역 초기화 함수
  const clearAllSelections = () => {
    setSelectedCells(new Set());
    setPendingChanges(new Map());
    setSubmissionChanges(new Map());
    setHasChanges(false);
    // 모든 타이머 정리
    tapTimers.forEach(timer => clearTimeout(timer));
    setTapTimers(new Map());
  };
  
  // 상세 모달 상태
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(null);
  const [selectedAssignmentId, setSelectedAssignmentId] = useState<string | null>(null);
  const [selectedAssignmentType, setSelectedAssignmentType] = useState<'assignment' | 'project' | 'routine' | null>(null);
  const [selectedStudentName, setSelectedStudentName] = useState<string>('');
  const [selectedStudentNumber, setSelectedStudentNumber] = useState<string>('');
  const [selectedAssignmentTitle, setSelectedAssignmentTitle] = useState<string>('');
  const [currentStatus, setCurrentStatus] = useState<string>('pending');
  const [currentPoints, setCurrentPoints] = useState<number>(0);
  const [currentMemo, setCurrentMemo] = useState<string>('');
  const [currentSubmissionLink, setCurrentSubmissionLink] = useState<string>('');
  const [currentSubmissionMessage, setCurrentSubmissionMessage] = useState<string>('');
  const [currentSubmittedAt, setCurrentSubmittedAt] = useState<Date | undefined>(undefined);
  const [currentSubmissionImagePath, setCurrentSubmissionImagePath] = useState<string | undefined>(undefined);
  const [currentSubmissionImageUpdatedAt, setCurrentSubmissionImageUpdatedAt] = useState<string | undefined>(
    undefined
  );
  const [currentIsDeleted, setCurrentIsDeleted] = useState<boolean>(false);
  
  // 네비게이션 관련 상태
  const [currentStudentIndex, setCurrentStudentIndex] = useState<number>(0);

  // 일반 과제 체크 모달 상태
  const [isGeneralAssignmentCheckModalOpen, setIsGeneralAssignmentCheckModalOpen] = useState(false);
  const [selectedGeneralAssignment, setSelectedGeneralAssignment] = useState<any>(null);
  const [selectedGeneralAssignmentStatuses, setSelectedGeneralAssignmentStatuses] = useState<any[]>([]);

  // 프로젝트 과제 체크 모달 상태
  const [isProjectAssignmentCheckModalOpen, setIsProjectAssignmentCheckModalOpen] = useState(false);
  const [selectedProjectAssignmentForTable, setSelectedProjectAssignmentForTable] = useState<any>(null);
  const [selectedProjectSubTasksForTable, setSelectedProjectSubTasksForTable] = useState<any[]>([]);
  const [selectedSubTaskIdForTable, setSelectedSubTaskIdForTable] = useState<string>('');
  const [selectedProjectAssignmentStatuses, setSelectedProjectAssignmentStatuses] = useState<any[]>([]);
  const [selectedProjectAssignmentIdForTable, setSelectedProjectAssignmentIdForTable] = useState<string>('');

  // 루틴 과제 체크 모달 상태
  const [isRoutineAssignmentCheckModalOpen, setIsRoutineAssignmentCheckModalOpen] = useState(false);
  const [selectedRoutineAssignmentForTable, setSelectedRoutineAssignmentForTable] = useState<any>(null);
  const [selectedRoutineDateForTable, setSelectedRoutineDateForTable] = useState<Date>(new Date());
  const [selectedRoutineAssignmentStatuses, setSelectedRoutineAssignmentStatuses] = useState<any[]>([]);
  const [selectedRoutineAssignmentIdForTable, setSelectedRoutineAssignmentIdForTable] = useState<string>('');

  // 날짜를 YYYY-MM-DD 형식의 ID로 변환 (한국 시간대 고려)
  const dateToDateId = (date: Date): string => {
    const koreaTime = new Date(date.getTime() + (9 * 60 * 60 * 1000));
    const dateStr = koreaTime.getUTCFullYear() + '-' + 
      String(koreaTime.getUTCMonth() + 1).padStart(2, '0') + '-' + 
      String(koreaTime.getUTCDate()).padStart(2, '0');
    return dateStr;
  };

  // 최근 8일 날짜 목록 생성 (한국 시간대 고려)
  // 최신 날짜가 왼쪽, 오래된 날짜가 오른쪽으로 정렬
  const getRecentDates = () => {
    const dates = [];
    const today = new Date();
    
    for (let i = 0; i < 8; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      
      // 한국 시간대 기준으로 dateId 계산
      const dateId = dateToDateId(date);
      
      // 실제 오늘 날짜와 비교하여 isToday 설정
      const isToday = date.toDateString() === today.toDateString();
      
      dates.push({
        date: new Date(date),
        dateId: dateId,
        dayOfWeek: ['일', '월', '화', '수', '목', '금', '토'][date.getDay()],
        isToday: isToday
      });
    }
    
    // 날짜를 내림차순으로 정렬 (최신 날짜가 먼저)
    return dates.sort((a, b) => b.date.getTime() - a.date.getTime());
  };

  // 루틴 과제 색상 가져오기
  const getRoutineAssignmentColor = () => {
    if (!tableData || filterAssignmentType !== 'routine') return '#6366f1'; // 기본 루틴 과제 색상
    
    const routineAssignments = tableData.allAssignments.filter(assignment => assignment.type === 'routine');
    if (routineAssignments.length === 0) return '#6366f1'; // 루틴 과제가 없어도 기본 색상 유지
    
    // 첫 번째 루틴 과제의 색상 사용 (모든 루틴 과제가 같은 색상이라고 가정)
    return routineAssignments[0].color || '#6366f1';
  };

  // 셀 선택/해제 함수
  const toggleCellSelection = (studentId: string, assignmentId: string) => {
    const cellKey = `${studentId}-${assignmentId}`;
    setSelectedCells(prev => {
      const newSet = new Set(prev);
      if (newSet.has(cellKey)) {
        newSet.delete(cellKey);
      } else {
        newSet.add(cellKey);
      }
      return newSet;
    });
  };

  // 상태 변경 함수
  // 키보드 단축키 이벤트 핸들러 (1,2,3,4 키로 상태 변경)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // 입력 필드에서는 동작하지 않음
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') {
        return;
      }
      
      // 선택된 셀이 없으면 동작하지 않음
      if (selectedCells.size === 0) return;
      
      // 1, 2, 3, 4 키 처리
      const statusMap: { [key: string]: string } = {
        '1': 'pending',    // 미확인
        '2': 'confirmed',  // 확인
        '3': 'delayed',    // 지연확인
        '4': 'exempted'    // 면제
      };
      
      const newStatus = statusMap[e.key];
      if (newStatus) {
        e.preventDefault();
        changeSelectedCellsStatusRef.current(newStatus);
        console.log(`⌨️ 단축키 ${e.key}: 선택된 ${selectedCells.size}개 셀을 '${newStatus}' 상태로 변경`);
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedCells]);

  function changeSelectedCellsStatus(newStatus: string) {
    setPendingChanges(prev => {
      const newMap = new Map(prev);
      selectedCells.forEach(cellKey => {
        // 기존 데이터가 있으면 유지하고 상태만 업데이트
        const existing = newMap.get(cellKey);
        
        // 상태에 따른 포인트 계산
        let calculatedPoints = 0;
        if (newStatus === 'pending') {
          calculatedPoints = 0;
        } else if (newStatus === 'confirmed') {
          // 과제 설정 포인트 - 테이블 데이터에서 직접 가져오기
          const assignment = tableData?.allAssignments.find(a => {
            if (a.type === 'assignment') {
              return cellKey.includes(`-${a.id}`);
            } else if (a.type === 'project') {
              return cellKey.includes(`-${a.id}-${a.subTaskId}`);
            } else if (a.type === 'routine') {
              return cellKey.includes(`-${a.id}`);
            }
            return false;
          });
          
          if (assignment) {
            if (assignment.type === 'assignment') {
              calculatedPoints = assignment.basePoints || 0;
            } else if (assignment.type === 'project') {
              calculatedPoints = assignment.basePoints || 0;
            } else if (assignment.type === 'routine') {
              calculatedPoints = assignment.basePoints || 0;
            }
          }
        } else if (newStatus === 'delayed' || newStatus === 'exempted') {
          // 환경설정 포인트
          if (statusDefaultPoints && statusDefaultPoints[newStatus]) {
            calculatedPoints = statusDefaultPoints[newStatus];
          }
        }
        
        newMap.set(cellKey, {
          status: newStatus,
          points: calculatedPoints,
          memo: existing?.memo || ''
        });
      });
      return newMap;
    });
    
    // 상태 변경 후 선택 초기화
    setSelectedCells(new Set());
  }

  changeSelectedCellsStatusRef.current = changeSelectedCellsStatus;

  // 더블탭 감지 함수
  const handleCellDoubleTap = (studentId: string, assignmentId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // 과제를 부여받지 않은 멤버(회색 블럭)인지 확인
    if (!tableData) return;
    
    const student = tableData.studentStatuses.find(s => s.studentId === studentId);
    if (!student) return;
    
    const assignmentStatus = student.assignmentStatuses[assignmentId];
    const isAssigned = assignmentStatus?.isAssigned !== false;
    
    // 과제를 부여받지 않은 멤버의 경우 더블탭 무시
    if (!isAssigned) {
      console.log('🚫 과제를 부여받지 않은 멤버의 셀 더블탭 무시:', { studentId, assignmentId });
      return;
    }
    
    // 과제 세부 조정 모달 열기 (나중에 구현)
    console.log('더블탭 감지:', studentId, assignmentId);
  };

  // 셀 클릭 처리 함수
  const handleCellClick = (studentId: string, assignmentId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // 과제를 부여받지 않은 멤버(회색 블럭)인지 확인
    if (!tableData) return;
    
    const student = tableData.studentStatuses.find(s => s.studentId === studentId);
    if (!student) return;
    
    const assignmentStatus = student.assignmentStatuses[assignmentId];
    const isAssigned = assignmentStatus?.isAssigned !== false;
    
    // 과제를 부여받지 않은 멤버의 경우 클릭 무시
    if (!isAssigned) {
      console.log('🚫 과제를 부여받지 않은 멤버의 셀 클릭 무시:', { studentId, assignmentId });
      return;
    }
    
    const cellKey = `${studentId}-${assignmentId}`;
    const existingTimer = tapTimers.get(cellKey);
    
    if (existingTimer) {
      // 더블탭 감지
      clearTimeout(existingTimer);
      setTapTimers(prev => {
        const newMap = new Map(prev);
        newMap.delete(cellKey);
        return newMap;
      });
      handleCellDoubleTap(studentId, assignmentId, e);
    } else {
      // 단일탭 - 즉시 셀 선택
      toggleCellSelection(studentId, assignmentId);
      
      // 더블탭 감지를 위한 타이머 설정 (300ms 후 제거)
      const timer = setTimeout(() => {
        setTapTimers(prev => {
          const newMap = new Map(prev);
          newMap.delete(cellKey);
          return newMap;
        });
      }, 300);
      
      setTapTimers(prev => new Map(prev).set(cellKey, timer));
    }
  };

  const detailModalSetters = {
    setCurrentStatus,
    setCurrentPoints,
    setCurrentMemo,
    setCurrentSubmissionLink,
    setCurrentSubmissionMessage,
    setCurrentSubmittedAt,
    setCurrentSubmissionImagePath,
    setCurrentSubmissionImageUpdatedAt,
    setCurrentIsDeleted
  };

  const loadAndApplyStudentDetail = async (
    studentId: string,
    assignment: AssignmentInfo,
    assignmentType: 'assignment' | 'project' | 'routine'
  ) => {
    if (!tableData || !user?.id || !currentClass?.id) return;

    const cacheKey = buildDetailCacheKey(studentId, assignment, assignmentType);
    const assignmentKey = getAssignmentStatusKey(assignment, assignmentType);

    if (memoEnrichPromiseRef.current) {
      await memoEnrichPromiseRef.current;
    }

    let loaded =
      getDetailFromStatusCache(statusDetailCacheRef.current, assignmentKey, studentId) ??
      ({
        status: tableData.studentStatuses.find((s) => s.studentId === studentId)?.assignmentStatuses[assignmentKey]
          ?.status || 'pending',
        points: 0,
        memo: '',
        submissionLink: '',
        submissionMessage: '',
        isDeleted: false
      } satisfies LoadedStudentDetail);

    const cacheHit = !!statusDetailCacheRef.current.get(assignmentKey)?.get(studentId);
    if (!cacheHit) {
      const fromServer = await loadStudentDetailFromServer(
        user.id,
        currentClass.id,
        studentId,
        assignment,
        assignmentType,
        currentMemberGroup
      );
      loaded = fromServer;
      ingestStatusesIntoCache(statusDetailCacheRef.current, assignmentKey, [
        loadedDetailToRawStatus(studentId, fromServer)
      ]);
    }

    const merged = mergeStudentDetailWithCaches(
      loaded,
      cacheKey,
      pendingChanges,
      submissionChanges,
      tableData
    );
    applyLoadedStudentDetailToSetter(merged, detailModalSetters);
  };

  // 셀 더블클릭으로 상세 모달 열기
  const handleCellDoubleClick = async (studentId: string, assignmentId: string, assignmentType: 'assignment' | 'project' | 'routine') => {
    console.log('🔍 셀 더블클릭:', { studentId, assignmentId, assignmentType });
    
    if (!tableData || !user?.id || !currentClass?.id) return;
    
    const student = tableData.studentStatuses.find(s => s.studentId === studentId);
    if (!student) return;
    
    const assignment = tableData.allAssignments.find(a => a.id === assignmentId);
    if (!assignment) return;

    const assignmentStatusKey = getAssignmentStatusKey(assignment, assignmentType);
    const assignmentStatus = student.assignmentStatuses[assignmentStatusKey];
    const isAssigned = assignmentStatus?.isAssigned !== false;
    
    if (!isAssigned) {
      console.log('🚫 과제를 부여받지 않은 멤버의 셀 더블클릭 무시:', { studentId, assignmentId });
      return;
    }
    
    const studentIndex = tableData.studentStatuses.findIndex(s => s.studentId === studentId);

    await loadAndApplyStudentDetail(studentId, assignment, assignmentType);
    
    setSelectedStudentId(studentId);
    setSelectedAssignmentId(assignmentId);
    setSelectedAssignmentType(assignmentType);
    setSelectedStudentName(student.studentName);
    setSelectedStudentNumber(student.studentNumber);
    setSelectedAssignmentTitle(assignment.title);
    setCurrentStudentIndex(studentIndex);
    setIsDetailModalOpen(true);
  };

  // 상세 모달 닫기
  const closeDetailModal = () => {
    setIsDetailModalOpen(false);
    setSelectedStudentId(null);
    setSelectedAssignmentId(null);
    // clearAllSelections(); // ❌ 캐시를 유지해야 하므로 호출하지 않음
    setSelectedAssignmentType(null);
    setSelectedStudentName('');
    setSelectedStudentNumber('');
    setSelectedAssignmentTitle('');
    setCurrentStatus('pending');
    setCurrentPoints(0);
    setCurrentMemo('');
    setCurrentSubmissionLink('');
    setCurrentSubmissionMessage('');
    setCurrentSubmittedAt(undefined);
    setCurrentSubmissionImagePath(undefined);
    setCurrentSubmissionImageUpdatedAt(undefined);
    setCurrentIsDeleted(false);
    setCurrentStudentIndex(0);
  };

  // 과제 제목 클릭 처리 함수 (더블탭 감지)
  const handleAssignmentTitleClick = (assignmentId: string, assignmentType: 'assignment' | 'project' | 'routine', subTaskId?: string) => {
    const cellKey = `assignment-${assignmentId}${subTaskId ? `-${subTaskId}` : ''}`;
    const existingTimer = tapTimers.get(cellKey);
    
    if (existingTimer) {
      // 더블탭 감지
      clearTimeout(existingTimer);
      setTapTimers(prev => {
        const newMap = new Map(prev);
        newMap.delete(cellKey);
        return newMap;
      });
      handleAssignmentTitleDoubleTap(assignmentId, assignmentType, subTaskId);
    } else {
      // 단일탭 - 즉시 과제 선택
      toggleAssignmentSelection(assignmentId);
      
      // 더블탭 감지를 위한 타이머 설정 (300ms 후 제거)
      const timer = setTimeout(() => {
        setTapTimers(prev => {
          const newMap = new Map(prev);
          newMap.delete(cellKey);
          return newMap;
        });
      }, 300);
      
      setTapTimers(prev => new Map(prev).set(cellKey, timer));
    }
  };

  // 과제 제목 더블탭 감지 함수
  const handleAssignmentTitleDoubleTap = (assignmentId: string, assignmentType: 'assignment' | 'project' | 'routine', subTaskId?: string) => {
    console.log('🔍 과제 제목 더블탭 감지:', { assignmentId, assignmentType, subTaskId });
    
    if (assignmentType === 'assignment') {
      handleGeneralAssignmentTitleDoubleClick(assignmentId);
    } else if (assignmentType === 'project' && subTaskId) {
      handleProjectAssignmentTitleDoubleClick(assignmentId, subTaskId);
    } else if (assignmentType === 'routine') {
      handleRoutineAssignmentTitleDoubleClick(assignmentId);
    }
  };

  // 일반 과제 제목 더블클릭 핸들러
  const handleGeneralAssignmentTitleDoubleClick = async (assignmentId: string) => {
    console.log('🔍 일반 과제 제목 더블클릭:', { assignmentId });
    
    if (!tableData || !user?.id || !currentClass?.id) return;

    try {
      // 캐싱된 tableData에서 해당 과제 정보 찾기
      const assignment = tableData.allAssignments.find(a => a.id === assignmentId && a.type === 'assignment');
      if (!assignment) {
        console.error('❌ 과제를 찾을 수 없습니다:', assignmentId);
        return;
      }

      console.log('✅ 캐싱된 과제 정보:', assignment);

      // assignmentService를 사용해 전체 과제 문서 로드 (멤버 상태 포함)
      const assignmentData = await assignmentService.loadAssignmentWithStatuses(
        user.id,
        currentClass.id,
        assignmentId
      );

      console.log('✅ 로드된 과제 문서:', assignmentData);

      // 캐시된 데이터를 우선적으로 사용하여 existingStatuses 생성
      const cachedStatuses = assignmentData.statuses?.map((status: any) => {
        const cellKey = `${status.studentId}-${assignmentId}`;
        const cachedData = pendingChanges.get(cellKey);
        
        if (cachedData) {
          console.log('🔍 캐시된 데이터 사용:', { studentId: status.studentId, cachedData });
          return {
            ...status,
            status: cachedData.status,
            earnedPoints: cachedData.points,
            memo: cachedData.memo
          };
        }
        
        return status;
      }) || [];

      console.log('✅ 캐시 반영된 멤버 상태:', cachedStatuses);

      ingestStatusesIntoCache(statusDetailCacheRef.current, assignmentId, cachedStatuses);

      // 과제 정보와 멤버 상태 설정
      setSelectedGeneralAssignment(assignmentData.assignment);
      setSelectedGeneralAssignmentStatuses(cachedStatuses);
      setIsGeneralAssignmentCheckModalOpen(true);

    } catch (error) {
      console.error('❌ 일반 과제 문서 로드 실패:', error);
      alert('과제 정보를 불러오는 중 오류가 발생했습니다.');
    }
  };

  // 루틴 과제 제목 더블클릭 핸들러
  const handleRoutineAssignmentTitleDoubleClick = async (assignmentId: string) => {
    console.log('🔍 루틴 과제 제목 더블클릭:', { assignmentId });
    
    if (!tableData || !user?.id || !currentClass?.id) return;

    try {
      // 캐싱된 tableData에서 해당 루틴 과제 정보 찾기
      const assignment = tableData.allAssignments.find(a => a.id === assignmentId && a.type === 'routine');
      if (!assignment) {
        console.error('❌ 루틴 과제를 찾을 수 없습니다:', assignmentId);
        return;
      }

      console.log('✅ 캐싱된 루틴 과제 정보:', assignment);

      // assignmentId에서 routineId와 날짜 추출 (routine_1758464086898-2025-09-25 -> routine_1758464086898, 2025-09-25)
      const routineId = assignment.routineAssignmentId || assignmentId.split('-').slice(0, -1).join('-');
      console.log('🔍 사용할 routineId:', routineId);
      
      // assignmentId에서 날짜 추출 (예: routine_1758464086898-2025-09-25 -> 2025-09-25)
      const dateMatch = assignmentId.match(/(\d{4}-\d{2}-\d{2})$/);
      let clickedDate: Date;
      if (dateMatch) {
        clickedDate = new Date(dateMatch[1] + 'T00:00:00');
        console.log('🔍 assignmentId에서 추출한 날짜:', clickedDate.toLocaleDateString('ko-KR'));
      } else {
        // 날짜 추출 실패 시 오늘 날짜 사용 (루틴 과제의 시작일이 오늘일 수 있음)
        clickedDate = new Date();
        clickedDate.setHours(0, 0, 0, 0);
        console.log('⚠️ 날짜 추출 실패, 오늘 날짜 사용:', clickedDate.toLocaleDateString('ko-KR'));
      }

      // routineAssignmentService를 사용해 전체 루틴 과제 문서 로드
      console.log('🔍 루틴 과제 문서 로드 시작...');
      const routineData = await routineAssignmentService.loadRoutineAssignmentWithStatuses(
        user.id,
        currentClass.id,
        routineId,
        currentMemberGroup
      );

      console.log('✅ 로드된 루틴 과제 문서:', routineData);

      if (!routineData.routine) {
        console.error('❌ 루틴 과제 문서가 비어있습니다:', routineData);
        alert('루틴 과제 정보가 비어있습니다.');
        return;
      }

      // 클릭한 날짜의 멤버 상태를 로드
      console.log('🔍 루틴 과제 날짜별 멤버 상태 로드 시작...');
      const dateId = dateToDateId(clickedDate);
      
      // 전체 날짜 상태에서 특정 날짜만 필터링
      const targetDateStatus = routineData.dateStatuses?.find((dateStatus: any) => dateStatus.dateId === dateId);
      let studentStatuses = targetDateStatus?.students ? Object.entries(targetDateStatus.students).map(([studentId, status]: [string, any]) => ({
        studentId,
        ...status
      })) : [];

      // 서버에 해당 날짜 데이터가 없으면 테이블에서 로드된 멤버 상태 사용
      if (studentStatuses.length === 0 && tableData?.studentStatuses) {
        console.log('🔍 서버에 날짜 데이터 없음, 테이블 데이터에서 멤버 상태 생성...');
        studentStatuses = tableData.studentStatuses.map(student => {
          // assignmentStatuses가 객체인 경우 해당 assignmentId로 직접 접근
          const assignmentStatus = student.assignmentStatuses?.[assignmentId];
          
          return {
            studentId: student.studentId,
            status: assignmentStatus?.status || 'pending',
            earnedPoints: 0, // 기본값 사용
            memo: '', // 기본값 사용
            updatedAt: new Date()
          };
        });
        console.log('✅ 테이블 데이터에서 생성된 멤버 상태:', studentStatuses);
      }

      console.log('✅ 로드된 루틴 과제 날짜별 멤버 상태:', studentStatuses);
      console.log('🔍 전체 pendingChanges 키들:', Array.from(pendingChanges.keys()));

      // 캐시된 데이터를 우선적으로 사용하여 existingStatuses 생성
      const cachedStatuses = studentStatuses.map((status: any) => {
        // 테이블에서 사용하는 cellKey 형식: studentId-assignmentId
        const cellKey = `${status.studentId}-${assignmentId}`;
        const cachedData = pendingChanges.get(cellKey);
        
        console.log('🔍 루틴 과제 캐시 확인:', {
          studentId: status.studentId,
          assignmentId,
          cellKey,
          hasCachedData: !!cachedData,
          cachedData,
          pendingChangesKeys: Array.from(pendingChanges.keys()).filter(key => key.includes(assignmentId))
        });
        
        if (cachedData) {
          console.log('🔍 루틴 과제 캐시된 데이터 사용:', { studentId: status.studentId, cachedData });
          return {
            ...status,
            status: cachedData.status,
            earnedPoints: cachedData.points,
            memo: cachedData.memo
          };
        }
        
        return status;
      });

      console.log('✅ 루틴 과제 캐시 반영된 멤버 상태:', cachedStatuses);

      ingestStatusesIntoCache(
        statusDetailCacheRef.current,
        getAssignmentStatusKey(assignment, 'routine'),
        cachedStatuses
      );

      // 루틴 과제 정보와 멤버 상태 설정
      setSelectedRoutineAssignmentForTable(routineData.routine);
      setSelectedRoutineDateForTable(clickedDate); // assignmentId에서 추출한 날짜 사용
      setSelectedRoutineAssignmentStatuses(cachedStatuses);
      setSelectedRoutineAssignmentIdForTable(routineId); // routineId 저장
      setIsRoutineAssignmentCheckModalOpen(true);

      console.log('✅ 루틴 과제 체크 모달 열기 완료');

    } catch (error) {
      console.error('❌ 루틴 과제 문서 로드 실패:', error);
      console.error('❌ 에러 상세:', {
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
        assignmentId,
        userId: user?.id,
        classId: currentClass?.id
      });
      alert(`루틴 과제 정보를 불러오는 중 오류가 발생했습니다: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  // 프로젝트 과제 제목 더블클릭 핸들러
  const handleProjectAssignmentTitleDoubleClick = async (assignmentId: string, subTaskId: string) => {
    console.log('🔍 프로젝트 과제 제목 더블클릭:', { assignmentId, subTaskId });
    
    if (!tableData || !user?.id || !currentClass?.id) {
      console.error('❌ 필수 데이터가 없습니다:', { 
        hasTableData: !!tableData, 
        hasUserId: !!user?.id, 
        hasClassId: !!currentClass?.id 
      });
      return;
    }

    try {
      // 캐싱된 tableData에서 해당 프로젝트 과제 정보 찾기
      const assignment = tableData.allAssignments.find(a => 
        a.id === assignmentId && a.type === 'project' && a.subTaskId === subTaskId
      );
      if (!assignment) {
        console.error('❌ 프로젝트 과제를 찾을 수 없습니다:', { 
          assignmentId, 
          subTaskId, 
          allAssignments: tableData.allAssignments.map(a => ({ id: a.id, type: a.type, subTaskId: a.subTaskId }))
        });
        alert('프로젝트 과제를 찾을 수 없습니다.');
        return;
      }

      console.log('✅ 캐싱된 프로젝트 과제 정보:', assignment);

      // projectId 추출 (assignmentId에서 하위 과제 ID 부분 제거)
      const projectId = assignmentId.split('-')[0]; // project_1758462184304-subtask-1758462176045 -> project_1758462184304
      console.log('🔍 사용할 projectId:', projectId);

      // projectAssignmentService를 사용해 전체 프로젝트 문서 로드
      console.log('🔍 프로젝트 문서 로드 시작...');
      const projectData = await projectAssignmentService.loadProjectAssignmentWithStatuses(
        user.id,
        currentClass.id,
        projectId
      );

      console.log('✅ 로드된 프로젝트 문서:', projectData);

      if (!projectData.projectAssignment) {
        console.error('❌ 프로젝트 문서가 비어있습니다:', projectData);
        alert('프로젝트 과제 정보가 비어있습니다.');
        return;
      }

      // 해당 하위 과제의 멤버 상태를 별도로 로드
      console.log('🔍 하위 과제 멤버 상태 로드 시작...');
      const studentStatuses = await projectAssignmentService.loadProjectAssignmentStudentStatuses(
        user.id,
        currentClass.id,
        projectId,
        subTaskId,
        currentMemberGroup
      );

      console.log('✅ 로드된 하위 과제 멤버 상태:', studentStatuses);

      // 캐시된 데이터를 우선적으로 사용하여 existingStatuses 생성
      const cachedStatuses = studentStatuses.map((status: any) => {
        const uniqueAssignmentId = `${assignmentId}-${subTaskId}`;
        const cellKey = `${status.studentId}-${uniqueAssignmentId}`;
        const cachedData = pendingChanges.get(cellKey);
        
        if (cachedData) {
          console.log('🔍 프로젝트 과제 캐시된 데이터 사용:', { studentId: status.studentId, cachedData });
          return {
            ...status,
            status: cachedData.status,
            earnedPoints: cachedData.points,
            memo: cachedData.memo
          };
        }
        
        return status;
      });

      console.log('✅ 프로젝트 과제 캐시 반영된 멤버 상태:', cachedStatuses);

      ingestStatusesIntoCache(
        statusDetailCacheRef.current,
        `${assignmentId}-${subTaskId}`,
        cachedStatuses
      );

      // 해당 하위 과제만의 정보로 설정 (네비게이션 없이)
      const targetSubTask = projectData.projectAssignment.subTasks?.find((st: any) => st.id === subTaskId);
      if (!targetSubTask) {
        console.error('❌ 하위 과제를 찾을 수 없습니다:', { 
          subTaskId, 
          availableSubTasks: projectData.projectAssignment.subTasks?.map((st: any) => st.id) 
        });
        alert('하위 과제를 찾을 수 없습니다.');
        return;
      }

      console.log('🔍 찾은 하위 과제 정보:', {
        subTaskId,
        targetSubTask: {
          id: targetSubTask.id,
          title: targetSubTask.title,
          points: targetSubTask.points
        }
      });

      setSelectedProjectAssignmentForTable(projectData.projectAssignment);
      setSelectedProjectSubTasksForTable([{ subTask: targetSubTask }]); // AssignmentCheckModal 형식에 맞게 래핑
      setSelectedSubTaskIdForTable(subTaskId);
      setSelectedProjectAssignmentStatuses(cachedStatuses);
      setSelectedProjectAssignmentIdForTable(assignmentId); // 테이블에서 사용하는 assignmentId 저장
      setIsProjectAssignmentCheckModalOpen(true);

      console.log('✅ 프로젝트 과제 체크 모달 열기 완료');

    } catch (error) {
      console.error('❌ 프로젝트 과제 문서 로드 실패:', error);
      console.error('❌ 에러 상세:', {
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
        assignmentId,
        subTaskId,
        userId: user?.id,
        classId: currentClass?.id
      });
      alert(`프로젝트 과제 정보를 불러오는 중 오류가 발생했습니다: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  // 멤버 네비게이션 함수
  const handleStudentNavigation = async (
    direction: 'prev' | 'next',
    currentState?: {
      status: string;
      points: number;
      memo: string;
      submissionLink?: string;
      submissionMessage?: string;
      isDeleted?: boolean;
      isImageDeleted?: boolean;
    }
  ) => {
    if (!tableData || !selectedAssignmentId || !selectedAssignmentType || !user?.id || !currentClass?.id) {
      return;
    }

    const assignment = tableData.allAssignments.find((a) => a.id === selectedAssignmentId);
    if (!assignment) return;

    const cacheKey = buildDetailCacheKey(
      selectedStudentId || '',
      assignment,
      selectedAssignmentType
    );
    if (selectedStudentId && currentState) {
      setPendingChanges((prev) =>
        new Map(prev).set(cacheKey, {
          status: currentState.status,
          points: currentState.points,
          memo: currentState.memo
        })
      );
      if (
        currentState.submissionLink !== undefined ||
        currentState.submissionMessage !== undefined ||
        currentState.isDeleted !== undefined
      ) {
        setSubmissionChanges((prev) =>
          new Map(prev).set(cacheKey, {
            link: currentState.submissionLink || '',
            message: currentState.submissionMessage || '',
            isDeleted: !!(currentState.isDeleted || currentState.isImageDeleted)
          })
        );
      }
    }

    const totalStudents = tableData.studentStatuses.length;
    let newIndex = currentStudentIndex;

    if (direction === 'prev') {
      newIndex = currentStudentIndex > 0 ? currentStudentIndex - 1 : currentStudentIndex;
    } else {
      newIndex = currentStudentIndex < totalStudents - 1 ? currentStudentIndex + 1 : currentStudentIndex;
    }

    if (newIndex === currentStudentIndex) return;

    const newStudent = tableData.studentStatuses[newIndex];
    if (!newStudent) return;

    await loadAndApplyStudentDetail(newStudent.studentId, assignment, selectedAssignmentType);

    setCurrentStudentIndex(newIndex);
    setSelectedStudentId(newStudent.studentId);
    setSelectedStudentName(newStudent.studentName);
    setSelectedStudentNumber(newStudent.studentNumber);
  };

  // 상세 모달 저장 (캐시에만 저장, 서버 저장은 테이블 저장 버튼에서)
  const handleDetailModalSave = async (status: string, points: number, memo: string, submissionData?: { link: string; message: string; isDeleted: boolean }) => {
    if (!selectedStudentId || !selectedAssignmentId || !selectedAssignmentType) return;
    
    console.log('💾 상세 모달 캐시 저장 시작:', { 
      studentId: selectedStudentId, 
      assignmentId: selectedAssignmentId, 
      assignmentType: selectedAssignmentType,
      status, 
      points, 
      memo,
      submissionData,
      hasSubmissionData: !!submissionData
    });
    
    try {
      // 과제 타입별 캐시 저장 로직
      if (selectedAssignmentType === 'assignment') {
        // 일반 과제 캐시 저장
        const cellKey = `${selectedStudentId}-${selectedAssignmentId}`;
        setPendingChanges(prev => new Map(prev).set(cellKey, { status, points, memo }));
        
        // 제출 확인 데이터 캐시 저장
        if (submissionData) {
          setSubmissionChanges(prev => {
            const newMap = new Map(prev);
            newMap.set(cellKey, submissionData);
            console.log('📦 제출 확인 데이터 캐시 저장:', { 
              cellKey, 
              submissionData, 
              mapSize: newMap.size,
              allKeys: Array.from(newMap.keys())
            });
            return newMap;
          });
        }
        
        console.log('✅ 일반 과제 캐시 저장 완료:', { cellKey, status, points, memo });
      } else if (selectedAssignmentType === 'project') {
        // 프로젝트 과제 캐시 저장
        const assignment = tableData?.allAssignments.find(a => a.id === selectedAssignmentId);
        const subTaskId = assignment?.subTaskId;
        
        if (subTaskId) {
          const uniqueAssignmentId = `${assignment.id}-${assignment.subTaskId}`;
          const cellKey = `${selectedStudentId}-${uniqueAssignmentId}`;
          setPendingChanges(prev => new Map(prev).set(cellKey, { status, points, memo }));
          
          // 제출 확인 데이터 캐시 저장
          if (submissionData) {
            setSubmissionChanges(prev => new Map(prev).set(cellKey, submissionData));
            console.log('📦 제출 확인 데이터 캐시 저장:', { cellKey, submissionData });
          }
          
          console.log('✅ 프로젝트 과제 캐시 저장 완료:', { cellKey, status, points, memo });
        }
      } else if (selectedAssignmentType === 'routine') {
        // 루틴 과제 캐시 저장
        const cellKey = `${selectedStudentId}-${selectedAssignmentId}`;
        setPendingChanges(prev => new Map(prev).set(cellKey, { status, points, memo }));
        
        // 제출 확인 데이터 캐시 저장
        if (submissionData) {
          setSubmissionChanges(prev => new Map(prev).set(cellKey, submissionData));
          console.log('📦 제출 확인 데이터 캐시 저장:', { cellKey, submissionData });
        }
        
        console.log('✅ 루틴 과제 캐시 저장 완료:', { cellKey, status, points, memo });
      }
      
      const assignment = tableData?.allAssignments.find((a) => a.id === selectedAssignmentId);
      if (assignment && selectedStudentId && selectedAssignmentType) {
        const assignmentKey = getAssignmentStatusKey(assignment, selectedAssignmentType);
        const prevRaw =
          statusDetailCacheRef.current.get(assignmentKey)?.get(selectedStudentId) || {};
        ingestStatusesIntoCache(statusDetailCacheRef.current, assignmentKey, [
          {
            ...prevRaw,
            studentId: selectedStudentId,
            status,
            points,
            earnedPoints: points,
            memo,
            ...(submissionData && {
              submissionLink: submissionData.link,
              submissionMessage: submissionData.message
            })
          }
        ]);
      }

      console.log('✅ 상세 모달 캐시 저장 완료 - 테이블 저장 버튼을 눌러 서버에 반영하세요');
    } catch (error) {
      console.error('❌ 상세 모달 캐시 저장 실패:', error);
      alert('캐시 저장 중 오류가 발생했습니다. 다시 시도해주세요.');
    }
  };

  // 과제 제목 클릭으로 전체 선택/해제
  const toggleAssignmentSelection = (assignmentId: string) => {
    if (!tableData) return;
    
    console.log('🔍 과제 제목 클릭:', { assignmentId, studentCount: tableData.studentStatuses.length });
    
    // 디버깅: 실제 테이블에서 사용되는 키들을 확인
    console.log('🔍 테이블의 모든 selectedCells:', Array.from(selectedCells));
    
    // 첫 번째 멤버의 assignmentStatuses 키들을 확인
    if (tableData.studentStatuses.length > 0) {
      const firstStudent = tableData.studentStatuses[0];
      console.log('🔍 첫 번째 멤버의 assignmentStatuses 키들:', Object.keys(firstStudent.assignmentStatuses));
    }
    
    // 프로젝트 과제의 경우 V4 서비스와 동일한 키 형식 사용
    let actualAssignmentId = assignmentId;
    if (assignmentId.includes('-subtask-')) {
      // assignmentId에서 subtask ID를 추출하여 V4 서비스와 동일한 키 형식 생성
      const subtaskIndex = assignmentId.indexOf('-subtask-');
      const subtaskId = assignmentId.substring(subtaskIndex + 1); // subtask-1758462176045
      actualAssignmentId = `${assignmentId}-${subtaskId}`; // project_1758462184304-subtask-1758462176045-subtask-1758462176045
      console.log('🔧 프로젝트 과제 ID 변환:', { original: assignmentId, subtaskId, converted: actualAssignmentId });
    }
    
    const isAllSelected = tableData.studentStatuses.every(student => 
      selectedCells.has(`${student.studentId}-${actualAssignmentId}`)
    );
    
    console.log('📊 전체 선택 상태:', { isAllSelected, selectedCount: selectedCells.size });
    
    setSelectedCells(prev => {
      const newSet = new Set(prev);
      tableData.studentStatuses.forEach(student => {
        const cellKey = `${student.studentId}-${actualAssignmentId}`;
        if (isAllSelected) {
          newSet.delete(cellKey);
          console.log('❌ 셀 선택 해제:', cellKey);
        } else {
          newSet.add(cellKey);
          console.log('✅ 셀 선택 추가:', cellKey);
        }
      });
      console.log('🔍 새로운 selectedCells:', Array.from(newSet));
      return newSet;
    });
  };

  // 포인트 계산 함수
  const calculatePoints = async (status: string, assignment: AssignmentInfo, userId: string, classId: string): Promise<number> => {
    try {
      // 1. 미확인: 포인트 0
      if (status === 'pending') {
        return 0;
      }
      
      // 2. 확인: 해당 과제문서에서 설정한 포인트
      if (status === 'confirmed') {
        try {
          if (assignment.type === 'assignment') {
            // 일반 과제의 경우 실제 과제 객체에서 포인트 가져오기
            const result = await assignmentService.loadAssignmentWithStatuses(userId, classId, assignment.id);
            return result.assignment?.basePoints || 0;
          } else if (assignment.type === 'project') {
            // 프로젝트 과제의 경우 하위 과제의 포인트를 가져와야 함
            const projectId = assignment.id.split('-')[0]; // project_1758462184304
            const subTaskId = assignment.subTaskId;
            if (subTaskId) {
              const projectAssignment = await projectAssignmentService.getProjectAssignment(userId, classId, projectId, currentMemberGroup);
              const subTask = projectAssignment?.subTasks?.find(st => st.id === subTaskId);
              return subTask?.points || 0; // SubTask에는 basePoints가 아닌 points 사용
            }
            return 0;
          } else if (assignment.type === 'routine') {
            // 루틴 과제의 경우 루틴 과제의 포인트를 가져와야 함
            const routineId = assignment.routineAssignmentId || assignment.id;
            const result = await routineAssignmentService.loadRoutineAssignmentWithStatuses(userId, classId, routineId, currentMemberGroup);
            return result.routine?.basePoints || 0;
          }
        } catch (error) {
          console.error('❌ 과제 정보 로드 실패:', error);
          return 0;
        }
        return 0;
      }
      
      // 3. 지연확인, 예외: 확인 포인트의 설정된 백분율만큼 지급 (반올림)
      if (status === 'delayed' || status === 'exempted') {
        const confirmedPoints = await (async () => {
          try {
            if (assignment.type === 'assignment') {
              const result = await assignmentService.loadAssignmentWithStatuses(userId, classId, assignment.id);
              return result.assignment?.basePoints || 0;
            }
            if (assignment.type === 'project') {
              const projectId = assignment.id.split('-')[0];
              const subTaskId = assignment.subTaskId;
              if (subTaskId) {
                const projectAssignment = await projectAssignmentService.getProjectAssignment(userId, classId, projectId, currentMemberGroup);
                const subTask = projectAssignment?.subTasks?.find(st => st.id === subTaskId);
                return subTask?.points || 0;
              }
              return 0;
            }
            if (assignment.type === 'routine') {
              const routineId = assignment.routineAssignmentId || assignment.id;
              const result = await routineAssignmentService.loadRoutineAssignmentWithStatuses(userId, classId, routineId, currentMemberGroup);
              return result.routine?.basePoints || 0;
            }
          } catch {
            return 0;
          }
          return 0;
        })();
        const settings = await userDataService.loadAssignmentSettings(userId, classId);
        const percent = settings?.statusDefaultPoints?.[status] ?? 0;
        return Math.round(confirmedPoints * (percent || 0) / 100);
      }
      
      return 0;
    } catch (error) {
      console.error('❌ 포인트 계산 실패:', error);
      return 0;
    }
  };

  // 기존 메모 가져오기 함수
  const getExistingMemo = async (studentId: string, assignment: AssignmentInfo, userId: string, classId: string): Promise<string> => {
    try {
      if (assignment.type === 'assignment') {
        return await assignmentMemoService.getMemo(userId, classId, assignment.id, studentId, currentMemberGroup);
      } else if (assignment.type === 'project') {
        // assignment.id가 이미 "projectId-subTaskId" 형식
        const assignmentKey = assignment.id;
        return await assignmentMemoService.getMemo(userId, classId, assignmentKey, studentId, currentMemberGroup);
      } else if (assignment.type === 'routine') {
        if (assignment.routineAssignmentId && assignment.dateId) {
          const assignmentKey = `${assignment.routineAssignmentId}-${assignment.dateId}`;
          return await assignmentMemoService.getMemo(userId, classId, assignmentKey, studentId, currentMemberGroup);
        }
        return '';
      }
      return '';
    } catch (error) {
      console.error('❌ 기존 메모 로드 실패:', error);
      return '';
    }
  };

  // 뒤로가기 핸들러 (변경사항 확인)
  const handleBack = () => {
    if (hasChanges) {
      setShowWarningModal(true);
    } else {
      onBack();
    }
  };
  
  // 저장하고 이동
  const handleSaveAndExit = async () => {
    await handleSave();
    setShowWarningModal(false);
    // 저장 후 항상 onBack 호출 (뒤로가기 버튼의 경우)
    onBack();
  };
  
  // 저장하지 않고 닫기(뒤로)
  const handleExitWithoutSave = () => {
    setShowWarningModal(false);
    onBack();
  };

  const handleCloseWarningOnly = () => {
    setShowWarningModal(false);
  };

  // 저장 함수
  const handleSave = async () => {
    if (!hasChanges || (pendingChanges.size === 0 && submissionChanges.size === 0)) return;
    
    try {
      console.log('저장할 변경사항:', pendingChanges);
      
      // 과제 타입별로 그룹화
      const changesByType: { 
        assignment: { [assignmentId: string]: { [studentId: string]: string } },
        project: { [assignmentId: string]: { [subTaskId: string]: { [studentId: string]: string } } },
        routine: { [assignmentId: string]: { [studentId: string]: string } }
      } = {
        assignment: {},
        project: {},
        routine: {}
      };
      
      // pendingChanges를 과제 타입별로 그룹화
      pendingChanges.forEach((changeData, cellKey) => {
        const { status: newStatus } = changeData;
        // cellKey는 "studentId-assignmentId" 형태
        // studentId는 "member-{timestamp}" 형태
        // assignmentId는 "general_{timestamp}", "project_{timestamp}-subtask-{timestamp}", "routine_{timestamp}-{dateId}" 형태
        
        let studentId: string;
        let assignmentKey: string;
        
        // assignmentId 패턴으로 분할
        if (cellKey.includes('-general_')) {
          const generalIndex = cellKey.indexOf('-general_');
          studentId = cellKey.substring(0, generalIndex);
          assignmentKey = cellKey.substring(generalIndex + 1);
        } else if (cellKey.includes('-project_')) {
          const projectIndex = cellKey.indexOf('-project_');
          studentId = cellKey.substring(0, projectIndex);
          assignmentKey = cellKey.substring(projectIndex + 1);
        } else if (cellKey.includes('-routine_')) {
          const routineIndex = cellKey.indexOf('-routine_');
          studentId = cellKey.substring(0, routineIndex);
          assignmentKey = cellKey.substring(routineIndex + 1);
        } else {
          // 기본 분할 (첫 번째 '-'로)
          const firstDashIndex = cellKey.indexOf('-');
          studentId = cellKey.substring(0, firstDashIndex);
          assignmentKey = cellKey.substring(firstDashIndex + 1);
        }
        
        console.log('🔍 처리 중인 셀:', { cellKey, studentId, assignmentKey, newStatus });
        
        // assignmentKey에서 과제 타입과 ID 추출
        const assignment = tableData?.allAssignments.find(a => {
          if (a.type === 'assignment') {
            return a.id === assignmentKey;
          } else if (a.type === 'project') {
            return `${a.id}-${a.subTaskId}` === assignmentKey;
          } else if (a.type === 'routine') {
            return a.id === assignmentKey;
          }
          return false;
        });
        
        console.log('🔍 찾은 과제:', assignment);
        
        if (assignment) {
          if (!changesByType[assignment.type][assignment.id]) {
            changesByType[assignment.type][assignment.id] = {};
          }
          
          if (assignment.type === 'project') {
            // 프로젝트 과제의 경우 subTaskId도 포함
            const subTaskId = assignment.subTaskId || '';
            if (!changesByType[assignment.type][assignment.id][subTaskId]) {
              changesByType[assignment.type][assignment.id][subTaskId] = {};
            }
            (changesByType[assignment.type][assignment.id][subTaskId] as { [studentId: string]: string })[studentId] = newStatus;
            console.log('✅ 프로젝트 과제 그룹화 완료:', { assignmentId: assignment.id, subTaskId, studentId, status: newStatus });
          } else {
            (changesByType[assignment.type][assignment.id] as { [studentId: string]: string })[studentId] = newStatus;
            console.log('✅ 일반/루틴 과제 그룹화 완료:', { assignmentId: assignment.id, studentId, status: newStatus, type: assignment.type });
          }
        } else {
          console.error('❌ 과제를 찾을 수 없음:', { assignmentKey, availableAssignments: tableData?.allAssignments.map(a => ({ id: a.id, type: a.type, subTaskId: a.subTaskId })) });
        }
      });
      
      console.log('🔍 그룹화된 변경사항:', changesByType);
      
      // 각 과제 타입별로 저장
      for (const [assignmentType, assignments] of Object.entries(changesByType)) {
        console.log(`🚀 ${assignmentType} 타입 저장 시작:`, assignments);
        for (const [assignmentId, students] of Object.entries(assignments)) {
          console.log(`🚀 ${assignmentType} 과제 ${assignmentId} 저장 시작:`, students);
          
          if (assignmentType === 'assignment') {
            // 일반 과제 상태 저장
            const assignmentStudents = changesByType.assignment[assignmentId];
            if (assignmentStudents) {
              console.log(`📝 일반 과제 ${assignmentId} 멤버들:`, assignmentStudents);
              
              if (useOptimizedService) {
                // 최적화된 서비스 사용 - 일괄 업데이트
                const studentUpdates = await Promise.all(
                  Object.entries(assignmentStudents).map(async ([studentId, status]) => {
                    const assignment = tableData?.allAssignments.find(a => a.id === assignmentId);
                    // 상세 모달에서 저장된 포인트와 메모가 있으면 사용, 없으면 계산
                    const cellKey = `${studentId}-${assignmentId}`;
                    const changeData = pendingChanges.get(cellKey);
                    const submissionData = submissionChanges.get(cellKey);
                    const earnedPoints = changeData?.points !== undefined ? changeData.points : (assignment ? await calculatePoints(status, assignment, userId, classId) : 0);
                    const memo = changeData?.memo !== undefined ? changeData.memo : (assignment ? await getExistingMemo(studentId, assignment, userId, classId) : '');
                    
                    const update: any = {
                      studentId,
                      status: status as 'pending' | 'confirmed' | 'delayed' | 'exempted' | 'excluded',
                      earnedPoints,
                      memo
                    };
                    
                    // 제출 확인 데이터 포함 (있는 경우)
                    if (submissionData) {
                      if (submissionData.isDeleted) {
                        update.submissionLink = undefined;
                        update.submissionMessage = undefined;
                        update.submittedAt = undefined;
                      } else {
                        if (submissionData.link) update.submissionLink = submissionData.link;
                        if (submissionData.message) update.submissionMessage = submissionData.message;
                      }
                    }
                    
                    return update;
                  })
                );
                await assignmentService.batchUpdateStudentStatusesOptimized(
                  userId,
                  classId,
                  assignmentId,
                  studentUpdates
                );
                console.log('✅ 최적화된 일반 과제 상태 일괄 저장 완료:', { assignmentId, studentCount: studentUpdates.length });
              } else {
                // 기존 서비스 사용 - 개별 업데이트
                for (const [studentId, status] of Object.entries(assignmentStudents)) {
                  const assignment = tableData?.allAssignments.find(a => a.id === assignmentId);
                  // 상세 모달에서 저장된 포인트와 메모가 있으면 사용, 없으면 계산
                  const cellKey = `${studentId}-${assignmentId}`;
                  const changeData = pendingChanges.get(cellKey);
                  const earnedPoints = changeData?.points !== undefined ? changeData.points : (assignment ? await calculatePoints(status, assignment, userId, classId) : 0);
                  const memo = changeData?.memo !== undefined ? changeData.memo : (assignment ? await getExistingMemo(studentId, assignment, userId, classId) : '');
                  console.log(`💾 일반 과제 상태 저장 시도:`, { assignmentId, studentId, status, earnedPoints, memo });
                  await assignmentService.updateStudentStatus(
                    userId,
                    classId,
                    assignmentId,
                    studentId,
                    { 
                      status: status as 'pending' | 'confirmed' | 'delayed' | 'exempted' | 'excluded',
                      earnedPoints,
                      memo
                    }
                  );
                  console.log('✅ 일반 과제 상태 저장 완료:', { assignmentId, studentId, status, earnedPoints, memo });
                }
              }
            } else {
              console.log(`❌ 일반 과제 ${assignmentId}에 멤버 데이터가 없음`);
            }
          } else if (assignmentType === 'project') {
            // 프로젝트 과제 상태 저장
            const projectStudents = changesByType.project[assignmentId];
            if (projectStudents) {
              console.log(`📝 프로젝트 과제 ${assignmentId} 멤버들:`, projectStudents);
              for (const [subTaskId, subTaskStudents] of Object.entries(projectStudents)) {
                if (useOptimizedService) {
                  // 최적화된 서비스 사용
                  // assignmentId에서 실제 프로젝트 ID 추출
                  let actualProjectId = assignmentId;
                  if (assignmentId.includes('-subtask-')) {
                    // project_1758462184304-subtask-1758462176045 -> project_1758462184304
                    actualProjectId = assignmentId.substring(0, assignmentId.lastIndexOf('-subtask-'));
                  }
                  console.log(`🔍 프로젝트 ID 추출:`, { assignmentId, actualProjectId, subTaskId });
                  
                  // 하위 과제별로 일괄 업데이트
                  const studentUpdates = await Promise.all(
                    Object.entries(subTaskStudents).map(async ([studentId, status]) => {
                      const assignment = tableData?.allAssignments.find(a => a.id === assignmentId);
                      // 상세 모달에서 저장된 포인트와 메모가 있으면 사용, 없으면 계산
                      const uniqueAssignmentId = `${assignmentId}-${subTaskId}`;
                      const cellKey = `${studentId}-${uniqueAssignmentId}`;
                      const changeData = pendingChanges.get(cellKey);
                      const submissionData = submissionChanges.get(cellKey);
                      const earnedPoints = changeData?.points !== undefined ? changeData.points : (assignment ? await calculatePoints(status, assignment, userId, classId) : 0);
                      const memo = changeData?.memo !== undefined ? changeData.memo : (assignment ? await getExistingMemo(studentId, assignment, userId, classId) : '');
                      
                      const update: any = {
                        studentId,
                        status: status as 'pending' | 'confirmed' | 'delayed' | 'exempted' | 'excluded',
                        earnedPoints,
                        memo
                      };
                      
                      // 제출 확인 데이터 포함 (있는 경우)
                      if (submissionData) {
                        if (submissionData.isDeleted) {
                          update.submissionLink = undefined;
                          update.submissionMessage = undefined;
                          update.submittedAt = undefined;
                        } else {
                          if (submissionData.link) update.submissionLink = submissionData.link;
                          if (submissionData.message) update.submissionMessage = submissionData.message;
                        }
                      }
                      
                      return update;
                    })
                  );
                  
                  console.log(`💾 최적화된 프로젝트 과제 일괄 저장 시도:`, { assignmentId: actualProjectId, subTaskId, studentCount: studentUpdates.length });
                  await projectAssignmentService.batchUpdateSubTaskStudentStatuses(
                    userId,
                    classId,
                    actualProjectId,
                    subTaskId,
                    studentUpdates,
                    currentMemberGroup
                  );
                  console.log('✅ 최적화된 프로젝트 과제 상태 일괄 저장 완료:', { assignmentId: actualProjectId, subTaskId, studentCount: studentUpdates.length });
                } else {
                  // 기존 서비스 사용
                  for (const [studentId, status] of Object.entries(subTaskStudents)) {
                    const assignment = tableData?.allAssignments.find(a => a.id === assignmentId);
                    // 상세 모달에서 저장된 포인트와 메모가 있으면 사용, 없으면 계산
                    const uniqueAssignmentId = `${assignmentId}-${subTaskId}`;
                    const cellKey = `${studentId}-${uniqueAssignmentId}`;
                    const changeData = pendingChanges.get(cellKey);
                    const earnedPoints = changeData?.points !== undefined ? changeData.points : (assignment ? await calculatePoints(status, assignment, userId, classId) : 0);
                    const memo = changeData?.memo !== undefined ? changeData.memo : (assignment ? await getExistingMemo(studentId, assignment, userId, classId) : '');
                    console.log(`💾 프로젝트 과제 상태 저장 시도:`, { assignmentId, subTaskId, studentId, status, earnedPoints, memo });
                    await projectAssignmentService.updateProjectAssignmentStudentStatus(
                      userId,
                      classId,
                      assignmentId,
                      subTaskId,
                      studentId,
                      status as 'pending' | 'confirmed' | 'delayed' | 'exempted' | 'excluded',
                      earnedPoints,
                      memo,
                      currentMemberGroup
                    );
                    console.log('✅ 프로젝트 과제 상태 저장 완료:', { assignmentId, subTaskId, studentId, status, earnedPoints, memo });
                  }
                }
              }
            } else {
              console.log(`❌ 프로젝트 과제 ${assignmentId}에 멤버 데이터가 없음`);
            }
          } else if (assignmentType === 'routine') {
            // 루틴 과제 상태 저장 — 테이블 메타(routineAssignmentId, dateId) 우선, 폴백은 끝의 YYYY-MM-DD
            const routineAssignmentMeta = tableData?.allAssignments.find(a => a.id === assignmentId);
            let actualRoutineId = (routineAssignmentMeta?.routineAssignmentId || '').trim();
            let dateId = (routineAssignmentMeta?.dateId || '').trim();
            if (!dateId) {
              const dateMatch = assignmentId.match(/(\d{4}-\d{2}-\d{2})$/);
              if (dateMatch) dateId = dateMatch[1];
            }
            if (!actualRoutineId && dateId && assignmentId.endsWith(`-${dateId}`)) {
              actualRoutineId = assignmentId.slice(0, -(dateId.length + 1));
            }
            if (!actualRoutineId || !dateId) {
              console.error('루틴 과제 ID/날짜 파싱 실패:', {
                assignmentId,
                actualRoutineId,
                dateId,
                routineAssignmentMeta
              });
              alert('루틴 과제 식별 정보를 찾을 수 없어 저장할 수 없습니다.');
              continue;
            }
            console.log(`🔍 루틴 과제 ID 추출:`, { assignmentId, actualRoutineId, dateId });

            const routineStudents = changesByType.routine[assignmentId];
            if (routineStudents) {
              console.log(`📝 루틴 과제 ${assignmentId} 멤버들:`, routineStudents);

              if (useOptimizedService) {
                const studentUpdates = await Promise.all(
                  Object.entries(routineStudents).map(async ([studentId, status]) => {
                    const assignment = routineAssignmentMeta;
                    const cellKey = `${studentId}-${assignmentId}`;
                    const changeData = pendingChanges.get(cellKey);
                    const earnedPoints =
                      changeData?.points !== undefined
                        ? changeData.points
                        : assignment
                          ? await calculatePoints(status, assignment, userId, classId)
                          : 0;
                    const memo =
                      changeData?.memo !== undefined
                        ? changeData.memo
                        : assignment
                          ? await getExistingMemo(studentId, assignment, userId, classId)
                          : '';
                    return {
                      studentId,
                      status: status as 'pending' | 'confirmed' | 'delayed' | 'exempted' | 'excluded',
                      earnedPoints,
                      memo
                    };
                  })
                );
                console.log(`💾 최적화된 루틴 과제 일괄 저장 시도:`, {
                  assignmentId: actualRoutineId,
                  dateId,
                  studentCount: studentUpdates.length
                });
                await routineAssignmentService.batchUpdateRoutineAssignmentStatusOptimized(
                  userId,
                  classId,
                  actualRoutineId,
                  dateId,
                  studentUpdates,
                  currentMemberGroup,
                  {
                    fillMissingAssignedAsPending: false,
                    assignedStudentIds: routineAssignmentMeta?.assignedStudentIds || []
                  }
                );
                console.log('✅ 최적화된 루틴 과제 상태 일괄 저장 완료:', {
                  assignmentId: actualRoutineId,
                  dateId,
                  studentCount: studentUpdates.length
                });
              } else {
                for (const [studentId, status] of Object.entries(routineStudents)) {
                  const assignment = routineAssignmentMeta;
                  const cellKey = `${studentId}-${assignmentId}`;
                  const changeData = pendingChanges.get(cellKey);
                  const earnedPoints =
                    changeData?.points !== undefined
                      ? changeData.points
                      : assignment
                        ? await calculatePoints(status, assignment, userId, classId)
                        : 0;
                  const memo =
                    changeData?.memo !== undefined
                      ? changeData.memo
                      : assignment
                        ? await getExistingMemo(studentId, assignment, userId, classId)
                        : '';
                  console.log(`💾 루틴 과제 상태 저장 시도:`, {
                    assignmentId: actualRoutineId,
                    dateId,
                    studentId,
                    status,
                    earnedPoints,
                    memo
                  });
                  await routineAssignmentService.updateRoutineAssignmentStatus(
                    userId,
                    classId,
                    actualRoutineId,
                    dateId,
                    studentId,
                    { status, earnedPoints, memo } as any,
                    currentMemberGroup
                  );
                  console.log('✅ 루틴 과제 상태 저장 완료:', {
                    assignmentId: actualRoutineId,
                    dateId,
                    studentId,
                    status,
                    earnedPoints,
                    memo
                  });
                }
              }
            } else {
              console.log(`❌ 루틴 과제 ${assignmentId}에 멤버 데이터가 없음`);
            }
          }
        }
      }
      
      // 데이터 다시 로드 (기존 데이터 로드 로직과 동일)
      // 모달 안에서는 로딩 플래그를 켜지 않음 → 테이블이 사라졌다 나타나며 모달 높이가 깜박이는 현상 방지
      // pendingChanges는 로드 성공 후에만 비움 → 비우는 순간 tableData가 아직 옛값이면 잠깐 이전 상태가 보이는 현상 방지
      const loadData = async (): Promise<boolean> => {
        try {
          if (!embeddedInModal) {
            setLoading(true);
          }
          setError(null);
          const startTime = performance.now();

          let data: AssignmentTableData;
          let categoriesData: any[] = [];
          let projectsData: any[] = [];
          
          if (useOptimizedService) {
            // V4 최적화된 서비스 사용 (지연 로딩)
            if (filterAssignmentType === 'routine') {
              // V4 서비스에서 루틴 과제 로드 (날짜별 계산된 과제들)
              const assignmentType = 'routine';
              const v4Data = await assignmentTableServiceOptimizedV4.loadAssignmentTableData(userId, classId, assignmentType, selectedRoutineDate, currentMemberGroup) as AssignmentTableData;
              
              // V4 서비스에서 이미 선택된 날짜로 필터링된 데이터를 반환
              data = {
                allAssignments: v4Data.allAssignments,
                studentStatuses: v4Data.studentStatuses,
                incompleteAssignments: v4Data.incompleteAssignments,
                incompleteSubTasks: v4Data.incompleteSubTasks,
                statusDetailByAssignment: v4Data.statusDetailByAssignment
              } as AssignmentTableData;
            } else {
              // 과제 타입에 따라 지연 로딩
              const assignmentType = filterAssignmentType === 'assignment' ? 'general' : 
                                   filterAssignmentType === 'project' ? 'project' : 'general';
              const v4Data = await assignmentTableServiceOptimizedV4.loadAssignmentTableData(userId, classId, assignmentType, undefined, currentMemberGroup) as AssignmentTableData;
              
              // V4 서비스에서 반환된 데이터 구조 변환
              data = {
                allAssignments: v4Data.allAssignments,
                studentStatuses: v4Data.studentStatuses,
                incompleteAssignments: v4Data.incompleteAssignments,
                incompleteSubTasks: v4Data.incompleteSubTasks,
                statusDetailByAssignment: v4Data.statusDetailByAssignment
              } as AssignmentTableData;
              
              // V4 서비스에서 반환된 카테고리와 프로젝트 데이터
              categoriesData = v4Data.categoriesData || [];
              projectsData = v4Data.projectsData || [];
            }
          } else {
            // 기존 서비스 사용
            if (filterAssignmentType === 'routine') {
              data = await assignmentTableServiceOptimized.loadRoutineAssignmentTableData(userId, classId, selectedRoutineDate) as AssignmentTableData;
            } else {
              data = await assignmentTableServiceOptimized.loadAssignmentTableData(userId, classId) as AssignmentTableData;
            }
          }

          setTableData(data);
          setCategories(categoriesData);
          setProjects(projectsData);
          
          const endTime = performance.now();
          setLoadTime(endTime - startTime);
          setQueryCount(0);
          return true;
        } catch (error) {
          console.error('데이터 로드 실패:', error);
          setError(error instanceof Error ? error.message : '데이터를 불러오는데 실패했습니다.');
          return false;
        } finally {
          if (!embeddedInModal) {
            setLoading(false);
          }
        }
      };
      
      if (user?.id && currentClass?.id) {
        assignmentMemoService.invalidateMemoCache(user.id, currentClass.id, currentMemberGroup);
      }
      statusDetailCacheRef.current = new Map();
      memoEnrichPromiseRef.current = null;

      const reloadOk = await loadData();
      if (reloadOk) {
        setPendingChanges(new Map());
        setSubmissionChanges(new Map());
        setSelectedCells(new Set());
        setHasChanges(false);
      }
      
      console.log('저장 완료');
    } catch (error) {
      console.error('저장 실패:', error);
      alert('저장 중 오류가 발생했습니다. 다시 시도해주세요.');
    }
  };
  
  // handleSave가 변경될 때마다 ref 업데이트
  // handleSave는 일반 함수이므로 매번 새로운 참조가 생성되므로,
  // handleSave의 실제 로직이 변경될 때만 업데이트하도록 의존성 배열 조정
  useEffect(() => {
    handleSaveRef.current = handleSave;
    if (onSaveRef) {
      onSaveRef(handleSave);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasChanges, pendingChanges.size, submissionChanges.size, onSaveRef]);
  
  // 성능 모니터링
  const [, setLoadTime] = useState<number>(0);
  const [, setQueryCount] = useState<number>(0);
  
  // 최적화 옵션 (개발자 도구에서 제어 가능)
  const [useOptimizedService, setUseOptimizedService] = useState<boolean>(true);
  
  // 중복 실행 방지를 위한 ref
  const isLoadingRef = useRef<boolean>(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  // 데이터 로드 함수를 useCallback으로 메모이제이션
  const loadData = useCallback(async () => {
    if (!userId || !classId) return;
    
    // 이미 로딩 중이면 실행하지 않음
    if (isLoadingRef.current) {
      console.log('⏸️ 이미 로딩 중이므로 실행 건너뜀');
      return;
    }
    
    console.log('🔄 데이터 로드 함수 실행:', { userId, classId, filterAssignmentType, useOptimizedService });
    
    // 중복 실행 방지
    isLoadingRef.current = true;
    
    // 이전 요청 취소
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // 새로운 AbortController 생성
    abortControllerRef.current = new AbortController();
    
    try {
      setLoading(true);
      setError(null);
      const startTime = performance.now();
      
      // 과제 타입에 따라 다른 데이터 로드
      let data: AssignmentTableData;
      let categoriesData: any[] = [];
      let projectsData: any[] = [];
      
      if (useOptimizedService) {
        // V4 최적화된 서비스 사용 (지연 로딩)
        if (filterAssignmentType === 'allIncomplete') {
          // 미완료 전체: 3가지 타입의 데이터를 병렬로 로드
          const [generalData, projectData, routineData] = await Promise.all([
            assignmentTableServiceOptimizedV4.loadAssignmentTableData(userId, classId, 'general', new Date(), currentMemberGroup) as Promise<AssignmentTableData>,
            assignmentTableServiceOptimizedV4.loadAssignmentTableData(userId, classId, 'project', new Date(), currentMemberGroup) as Promise<AssignmentTableData>,
            assignmentTableServiceOptimizedV4.loadAssignmentTableData(userId, classId, 'routine', new Date(), currentMemberGroup) as Promise<AssignmentTableData>
          ]);
          
          // 미완료 과제만 필터링 (부여받은 멤버만 카운트, assignedStudentIds 우선)
          const getKey = (a: AssignmentInfo) => a.type === 'project' && a.subTaskId ? `${a.id}-${a.subTaskId}` : a.type === 'routine' && a.routineAssignmentId && a.dateId ? `${a.routineAssignmentId}-${a.dateId}` : a.id;
          const filterIncomplete = (assignments: AssignmentInfo[], studentStatuses: any[]) => {
            return assignments.filter(assignment => {
              const assignmentId = getKey(assignment);
              const ids = assignment.assignedStudentIds;
              let isAllCompleted: boolean;
              if (ids && ids.length > 0) {
                isAllCompleted = ids.every(memberId => {
                  const ss = studentStatuses?.find((s: any) => s.studentId === memberId);
                  const status = ss?.assignmentStatuses?.[assignmentId]?.status;
                  return status === 'confirmed' || status === 'delayed' || status === 'exempted';
                });
              } else {
                const assigned = studentStatuses?.filter((ss: any) => ss.assignmentStatuses?.[assignmentId]?.isAssigned === true) ?? [];
                isAllCompleted = assigned.length === 0 || assigned.every((ss: any) => {
                  const status = ss.assignmentStatuses?.[assignmentId]?.status;
                  return status === 'confirmed' || status === 'delayed' || status === 'exempted';
                });
              }
              return !isAllCompleted;
            });
          };
          
          // 각 타입에서 미완료 과제만 추출
          const incompleteGeneral = filterIncomplete(generalData.allAssignments || [], generalData.studentStatuses || []);
          const incompleteProject = filterIncomplete(projectData.allAssignments || [], projectData.studentStatuses || []);
          const incompleteRoutine = filterIncomplete(routineData.allAssignments || [], routineData.studentStatuses || []);
          
          // 모든 미완료 과제를 합침
          const allIncompleteAssignments = [...incompleteGeneral, ...incompleteProject, ...incompleteRoutine];
          
          // studentStatuses도 합침
          const combinedStudentStatuses = [...(generalData.studentStatuses || [])];
          projectData.studentStatuses?.forEach((ps: any) => {
            const existing = combinedStudentStatuses.find(s => s.studentId === ps.studentId);
            if (existing) {
              existing.assignmentStatuses = { ...existing.assignmentStatuses, ...ps.assignmentStatuses };
            } else {
              combinedStudentStatuses.push(ps);
            }
          });
          routineData.studentStatuses?.forEach((ps: any) => {
            const existing = combinedStudentStatuses.find(s => s.studentId === ps.studentId);
            if (existing) {
              existing.assignmentStatuses = { ...existing.assignmentStatuses, ...ps.assignmentStatuses };
            } else {
              combinedStudentStatuses.push(ps);
            }
          });
          
          data = {
            members: generalData.members || [],
            allAssignments: allIncompleteAssignments,
            studentStatuses: combinedStudentStatuses,
            incompleteAssignments: new Set(allIncompleteAssignments.map(a => a.id)),
            incompleteSubTasks: new Set<string>(),
            statusDetailByAssignment: {
              ...(generalData.statusDetailByAssignment || {}),
              ...(projectData.statusDetailByAssignment || {}),
              ...(routineData.statusDetailByAssignment || {})
            }
          } as AssignmentTableData;
          
        } else if (filterAssignmentType === 'routine') {
          // V4 서비스에서 루틴 과제 로드 (날짜별 계산된 과제들)
          const assignmentType = 'routine';
          const v4Data = await assignmentTableServiceOptimizedV4.loadAssignmentTableData(userId, classId, assignmentType, selectedRoutineDate, currentMemberGroup) as AssignmentTableData;
          
          // V4 서비스에서 이미 선택된 날짜로 필터링된 데이터를 반환
          data = {
            allAssignments: v4Data.allAssignments,
            studentStatuses: v4Data.studentStatuses,
            incompleteAssignments: v4Data.incompleteAssignments,
            incompleteSubTasks: v4Data.incompleteSubTasks,
            statusDetailByAssignment: v4Data.statusDetailByAssignment
          } as AssignmentTableData;
        } else {
          // 과제 타입에 따라 지연 로딩
          const assignmentType = filterAssignmentType === 'assignment' ? 'general' : 
                               filterAssignmentType === 'project' ? 'project' : 'general';
          const v4Data = await assignmentTableServiceOptimizedV4.loadAssignmentTableData(userId, classId, assignmentType, undefined, currentMemberGroup) as AssignmentTableData;
          
          // V4 서비스에서 반환된 데이터 구조 변환
          data = {
            allAssignments: v4Data.allAssignments,
            studentStatuses: v4Data.studentStatuses,
            incompleteAssignments: v4Data.incompleteAssignments,
            incompleteSubTasks: v4Data.incompleteSubTasks,
            statusDetailByAssignment: v4Data.statusDetailByAssignment
          } as AssignmentTableData;
          
          // V4 서비스에서 반환된 카테고리와 프로젝트 데이터
          categoriesData = v4Data.categoriesData || [];
          projectsData = v4Data.projectsData || [];
        }
        console.log(`🚀 V4 최적화된 서비스 사용 (지연 로딩 - ${filterAssignmentType} 과제만 로드)`);
      } else {
        // 기존 서비스 사용
        if (filterAssignmentType === 'allIncomplete') {
          // 기존 서비스는 미완료 전체 지원하지 않으므로 모든 과제 로드 후 필터링
          data = await assignmentTableServiceOptimized.loadAssignmentTableData(userId, classId) as AssignmentTableData;
        } else if (filterAssignmentType === 'routine') {
          data = await assignmentTableServiceOptimized.loadRoutineAssignmentTableData(userId, classId, selectedRoutineDate) as AssignmentTableData;
        } else {
          data = await assignmentTableServiceOptimized.loadAssignmentTableData(userId, classId) as AssignmentTableData;
        }
        console.log('📊 기존 서비스 사용');
      }
      
      // V4 서비스가 아닌 경우에만 추가 데이터 로드
      if (!useOptimizedService) {
        // 기존 서비스에서는 모든 데이터 로드
        [categoriesData, projectsData] = await Promise.all([
          assignmentCategoryService.loadCategories(userId, classId),
          projectAssignmentService.getProjectAssignments(userId, classId)
        ]);
      }
      
      const endTime = performance.now();
      setLoadTime(Math.round(endTime - startTime));
      
      // V4 서비스 사용 시 정확한 쿼리 수 계산
      console.log(`🔍 디버깅: useOptimizedService = ${useOptimizedService}`);
      if (useOptimizedService) {
        // Firebase 실제 읽기 횟수 사용
        const firebaseReads = firebaseReadCounter.getCount();
        setQueryCount(firebaseReads);
        console.log(`📊 V4 서비스 Firebase 읽기 횟수: ${firebaseReads}회`);
        console.log(`💰 예상 비용: $${(firebaseReads * 0.00006).toFixed(6)} (읽기 1회당 $0.00006)`);
      } else {
        // 기존 서비스 쿼리 수 계산
        const oldQueryCount = data.allAssignments.length + data.studentStatuses.length;
        setQueryCount(oldQueryCount);
        console.log(`📊 기존 서비스 쿼리 수: ${oldQueryCount}개 (과제: ${data.allAssignments.length} + 멤버상태: ${data.studentStatuses.length})`);
      }
      
      // 환경설정에서 기본 단위 포인트 및 상태별 기본 포인트 로드
      try {
        const settings = await userDataService.loadAssignmentSettings(userId, classId);
        setDefaultPoints(settings?.defaultPoints || 1);
        setStatusDefaultPoints(settings?.statusDefaultPoints || undefined);
        console.log('✅ 기본 단위 포인트 로드:', settings?.defaultPoints || 1);
        console.log('✅ 상태별 기본 포인트 로드:', settings?.statusDefaultPoints);
      } catch (error) {
        console.error('❌ 환경설정 로드 실패:', error);
        setDefaultPoints(1); // 기본값 사용
        setStatusDefaultPoints(undefined);
      }
      
      setTableData(data);
      setCategories(categoriesData);
      setProjects(projectsData);
      
      console.log('로드된 프로젝트 데이터:', projectsData);
    } catch (error) {
      // AbortError는 무시 (요청이 취소된 경우)
      if (error instanceof Error && error.name !== 'AbortError') {
        console.error('데이터 로드 실패:', error);
        setError('데이터를 불러오는 중 오류가 발생했습니다.');
      }
    } finally {
      setLoading(false);
      // 로딩 완료 후 ref 초기화
      isLoadingRef.current = false;
    }
  }, [userId, classId, filterAssignmentType, selectedRoutineDate, currentMemberGroup, useOptimizedService]);

  // 통합된 데이터 로드 useEffect
  useEffect(() => {
    loadData();
  }, [loadData]);

  // 컴포넌트 언마운트 시 정리
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      isLoadingRef.current = false;
    };
  }, []);

  // 브라우저 전역에 최적화 옵션 제어 함수 등록 (한 번만 실행)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).toggleOptimizedService = (enabled?: boolean) => {
        setUseOptimizedService(prev => {
          const newValue = enabled !== undefined ? enabled : !prev;
          console.log(`🚀 최적화 서비스 ${newValue ? '활성화' : '비활성화'}`);
          return newValue;
        });
      };
      
      (window as any).getOptimizedServiceStatus = () => {
        console.log(`현재 최적화 서비스 상태: ${useOptimizedService ? '활성화' : '비활성화'}`);
        return useOptimizedService;
      };
      
      console.log('🔧 최적화 서비스 제어 함수가 브라우저 전역에 등록되었습니다:');
      console.log('- toggleOptimizedService(true/false) - 최적화 서비스 활성화/비활성화');
      console.log('- getOptimizedServiceStatus() - 현재 상태 확인');
    }
  }, [useOptimizedService]);

  // 과제 타입 변경 시 필터 초기화 및 선택 상태 초기화
  useEffect(() => {
    if (filterAssignmentType !== 'assignment') {
      setFilterCategoryId('all');
    }
    if (filterAssignmentType !== 'project') {
      setFilterProjectId('all');
    }
    
    // 탭 변경 시 선택된 셀들 초기화
    setSelectedCells(new Set());
    setPendingChanges(new Map());
    setHasChanges(false);
    console.log('🔄 과제 타입 변경으로 선택 상태 초기화');
  }, [filterAssignmentType]);

  // 루틴 과제 드롭다운 외부 클릭 감지
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (routineDateDropdownRef.current && !routineDateDropdownRef.current.contains(event.target as Node)) {
        setIsRoutineDateDropdownOpen(false);
      }
    };

    if (isRoutineDateDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isRoutineDateDropdownOpen]);

  // 프로젝트별 색상 생성 함수
  const getProjectColor = (projectTitle: string) => {
    const colors = [
      '#4a90e2', '#7b68ee', '#20b2aa', '#ff6347', '#32cd32',
      '#ffd700', '#ff69b4', '#9370db', '#00ced1', '#ffa500'
    ];
    
    // 프로젝트 제목의 해시값을 기반으로 색상 선택
    let hash = 0;
    for (let i = 0; i < projectTitle.length; i++) {
      hash = projectTitle.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  // 테이블 데이터에서 프로젝트 정보 추출
  useEffect(() => {
    if (tableData) {
      const projectMap = new Map();
      
      tableData.allAssignments.forEach(assignment => {
        if (assignment.type === 'project' && assignment.projectTitle) {
          // 프로젝트 ID를 생성 (projectTitle을 기반으로)
          const projectId = assignment.projectTitle.replace(/\s+/g, '-').toLowerCase();
          
          if (!projectMap.has(projectId)) {
            projectMap.set(projectId, {
              id: projectId,
              title: assignment.projectTitle,
              color: assignment.color || getProjectColor(assignment.projectTitle)
            });
          }
        }
      });
      
      const extractedProjectsList = Array.from(projectMap.values());
      setExtractedProjects(extractedProjectsList);
      
      console.log('추출된 프로젝트 목록:', extractedProjectsList);
    }
  }, [tableData]);

  // 존재하지 않는 카테고리 ID 감지 및 자동 리셋
  useEffect(() => {
    if (filterCategoryId !== 'all' && categories.length > 0) {
      const categoryExists = categories.some(cat => cat.id === filterCategoryId);
      if (!categoryExists) {
        setFilterCategoryId('all');
      }
    }
  }, [filterCategoryId, categories]);

  // 필터링된 과제들
  const filteredAssignments = useMemo(() => {
    if (!tableData) return [];

    let assignments = tableData.allAssignments;

    // 과제 타입 필터
    if (filterAssignmentType === 'allIncomplete') {
      // 미완료 전체: 모든 타입 포함 (이미 로드 시 미완료만 필터링됨)
      // 타입 필터 없이 모든 과제 유지
    } else {
      // 특정 타입으로 필터링
      const typeMapping: { [key: string]: string } = {
        'assignment': 'assignment',
        'project': 'project',
        'routine': 'routine'
      };
      const targetType = typeMapping[filterAssignmentType];
      assignments = assignments.filter(assignment => assignment.type === targetType);
    }

    // 카테고리 필터 (일반 과제만 - 미완료 전체에서는 적용 안함)
    if (filterAssignmentType !== 'allIncomplete' && filterCategoryId !== 'all') {
      assignments = assignments.filter(assignment => {
        if (assignment.type === 'assignment') {
          return assignment.categoryId === filterCategoryId;
        }
        return false; // 프로젝트 과제는 카테고리 필터링 시 제외
      });
    }

    // 프로젝트 필터 (프로젝트 과제만 - 미완료 전체에서는 적용 안함)
    if (filterAssignmentType !== 'allIncomplete' && filterProjectId !== 'all') {
      const selectedProject = extractedProjects.find(proj => proj.id === filterProjectId);
      assignments = assignments.filter(assignment => {
        if (assignment.type === 'project') {
          return assignment.projectTitle === selectedProject?.title;
        }
        return false; // 일반 과제는 프로젝트 필터링 시 제외
      });
    }

    // 완료 상태 필터 (미완료 전체에서는 적용 안함 - 이미 로드 시 미완료만 필터링됨). 부여받은 멤버만 기준으로 판별
    if (filterAssignmentType !== 'allIncomplete' && filterType === 'incomplete') {
      const getKey = (a: AssignmentInfo) => a.type === 'project' && a.subTaskId ? `${a.id}-${a.subTaskId}` : a.type === 'routine' && a.routineAssignmentId && a.dateId ? `${a.routineAssignmentId}-${a.dateId}` : a.id;
      assignments = assignments.filter(assignment => {
        const key = getKey(assignment);
        const ids = assignment.assignedStudentIds;
        if (ids && ids.length > 0) {
          const allCompleted = ids.every(memberId => {
            const ss = tableData.studentStatuses.find(s => s.studentId === memberId);
            const status = ss?.assignmentStatuses?.[key]?.status;
            return status === 'confirmed' || status === 'delayed' || status === 'exempted';
          });
          return !allCompleted;
        }
        const assigned = tableData.studentStatuses.filter(ss => ss.assignmentStatuses?.[key]?.isAssigned === true);
        const allCompleted = assigned.length === 0 || assigned.every(ss => {
          const status = ss.assignmentStatuses?.[key]?.status;
          return status === 'confirmed' || status === 'delayed' || status === 'exempted';
        });
        return !allCompleted;
      });
    }

    return assignments;
  }, [tableData, filterType, filterAssignmentType, filterCategoryId, filterProjectId, extractedProjects]);

  // 과제들을 타입별로 그룹화 및 마감일 기준 정렬 (최신 날짜가 왼쪽)
  const { regular, project, routine } = useMemo(() => {
    const regular: AssignmentInfo[] = [];
    const project: AssignmentInfo[] = [];
    const routine: AssignmentInfo[] = [];

    filteredAssignments.forEach(assignment => {
      if (assignment.type === 'assignment') {
        regular.push(assignment);
      } else if (assignment.type === 'project') {
        project.push(assignment);
      } else if (assignment.type === 'routine') {
        routine.push(assignment);
      }
    });

    // 마감일 기준 내림차순 정렬 (최신 날짜가 먼저, 왼쪽에 위치)
    const sortByDueDate = (a: AssignmentInfo, b: AssignmentInfo) => {
      const dateA = a.dueDate ? new Date(a.dueDate).getTime() : 0;
      const dateB = b.dueDate ? new Date(b.dueDate).getTime() : 0;
      return dateB - dateA; // 내림차순 (최신 날짜가 먼저)
    };

    regular.sort(sortByDueDate);
    // 프로젝트: 카드와 동일 - 프로젝트끼리는 생성일 최신→과거, 같은 프로젝트 내에서는 마지막 하위과제가 왼쪽
    project.sort((a, b) => {
      const projectIdA = a.id.replace(/-[^-]+$/, '');
      const projectIdB = b.id.replace(/-[^-]+$/, '');
      if (projectIdA !== projectIdB) {
        const timeA = a.projectCreatedAt?.getTime() ?? 0;
        const timeB = b.projectCreatedAt?.getTime() ?? 0;
        const byTime = timeB - timeA;
        if (byTime !== 0) return byTime;
        return projectIdA.localeCompare(projectIdB);
      }
      return (b.subTaskOrder ?? 0) - (a.subTaskOrder ?? 0); // 마지막 하위과제가 왼쪽
    });
    routine.sort(sortByDueDate);

    return { regular, project, routine };
  }, [filteredAssignments]);



  // 로딩 상태
  if (loading) {
    return (
      <Container $embeddedInModal={embeddedInModal}>
        <Header $embeddedInModal={embeddedInModal}>
          {!embeddedInModal && (
          <BackButton onClick={handleBack}>
            <svg 
              width="20" 
              height="20" 
              viewBox="0 0 24 24" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
            >
              <path 
                d="M15 18L9 12L15 6" 
                stroke="currentColor" 
                strokeWidth="3" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
            </svg>
          </BackButton>
        )}
          <Title $embeddedInModal={embeddedInModal}>과제 관리 테이블</Title>
          {embeddedInModal ? (
          <ModalCloseButton onClick={onBack} aria-label="모달 닫기">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </ModalCloseButton>
        ) : (
          <div style={{ width: '32px' }} />
        )}
        </Header>
        <Content $embeddedInModal={embeddedInModal}>
          {embeddedInModal ? (
            <ModalMessageFill>
              <LoadingText>데이터를 불러오는 중...</LoadingText>
            </ModalMessageFill>
          ) : (
            <LoadingText>데이터를 불러오는 중...</LoadingText>
          )}
        </Content>
      </Container>
    );
  }

  // 에러 상태
  if (error) {
    return (
      <Container $embeddedInModal={embeddedInModal}>
        <Header $embeddedInModal={embeddedInModal}>
          {!embeddedInModal && (
          <BackButton onClick={handleBack}>
            <svg 
              width="20" 
              height="20" 
              viewBox="0 0 24 24" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
            >
              <path 
                d="M15 18L9 12L15 6" 
                stroke="currentColor" 
                strokeWidth="3" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
            </svg>
          </BackButton>
        )}
          <Title $embeddedInModal={embeddedInModal}>과제 관리 테이블</Title>
          {embeddedInModal ? (
          <ModalCloseButton onClick={onBack} aria-label="모달 닫기">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </ModalCloseButton>
        ) : (
          <div style={{ width: '32px' }} />
        )}
        </Header>
        <Content $embeddedInModal={embeddedInModal}>
          {embeddedInModal ? (
            <ModalMessageFill>
              <ErrorText>{error}</ErrorText>
            </ModalMessageFill>
          ) : (
            <ErrorText>{error}</ErrorText>
          )}
        </Content>
      </Container>
    );
  }

  // 데이터 없음
  if (!tableData || tableData.studentStatuses.length === 0) {
    return (
      <Container $embeddedInModal={embeddedInModal}>
        <Header $embeddedInModal={embeddedInModal}>
          {!embeddedInModal && (
          <BackButton onClick={handleBack}>
            <svg 
              width="20" 
              height="20" 
              viewBox="0 0 24 24" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
            >
              <path 
                d="M15 18L9 12L15 6" 
                stroke="currentColor" 
                strokeWidth="3" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
            </svg>
          </BackButton>
        )}
          <Title $embeddedInModal={embeddedInModal}>과제 관리 테이블</Title>
          {embeddedInModal ? (
          <ModalCloseButton onClick={onBack} aria-label="모달 닫기">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </ModalCloseButton>
        ) : (
          <div style={{ width: '32px' }} />
        )}
        </Header>
        <Content $embeddedInModal={embeddedInModal}>
          {embeddedInModal ? (
            <ModalMessageFill>
              <EmptyText>멤버 데이터가 없습니다.</EmptyText>
            </ModalMessageFill>
          ) : (
            <EmptyText>멤버 데이터가 없습니다.</EmptyText>
          )}
        </Content>
      </Container>
    );
  }

  // 과제가 없는 경우
  if (filteredAssignments.length === 0) {
    // 필터링으로 인해 과제가 없는지 확인
    const isCategoryFiltered = filterCategoryId !== 'all';
    const isProjectFiltered = filterProjectId !== 'all';
    const selectedCategory = categories.find(cat => cat.id === filterCategoryId);
    const selectedProject = extractedProjects.find(proj => proj.id === filterProjectId);
    
    return (
      <Container $embeddedInModal={embeddedInModal}>
        <Header $embeddedInModal={embeddedInModal}>
          {!embeddedInModal && (
          <BackButton onClick={handleBack}>
            <svg 
              width="20" 
              height="20" 
              viewBox="0 0 24 24" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
            >
              <path 
                d="M15 18L9 12L15 6" 
                stroke="currentColor" 
                strokeWidth="3" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
            </svg>
          </BackButton>
        )}
          <Title $embeddedInModal={embeddedInModal}>과제 관리 테이블</Title>
          {embeddedInModal ? (
          <ModalCloseButton onClick={onBack} aria-label="모달 닫기">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </ModalCloseButton>
        ) : (
          <div style={{ width: '32px' }} />
        )}
        </Header>
        <Content $embeddedInModal={embeddedInModal}>
          <TopSection>
            <ToggleSection>
              <ToggleContainer $tabCount={4}>
                <ToggleSlider $position={
                  filterAssignmentType === 'allIncomplete' ? 0 :
                  filterAssignmentType === 'assignment' ? 1 : 
                  filterAssignmentType === 'project' ? 2 : 3
                } $tabCount={4} />
                <ToggleOption
                  $active={filterAssignmentType === 'allIncomplete'}
                  onClick={() => setFilterAssignmentType('allIncomplete')}
                >
                  <ToggleText>미완료 전체</ToggleText>
                  <ToggleTextShort>미완료</ToggleTextShort>
                </ToggleOption>
                <ToggleOption
                  $active={filterAssignmentType === 'assignment'}
                  onClick={() => setFilterAssignmentType('assignment')}
                >
                  <ToggleText>일반 과제</ToggleText>
                  <ToggleTextShort>일반</ToggleTextShort>
                </ToggleOption>
                <ToggleOption
                  $active={filterAssignmentType === 'project'}
                  onClick={() => setFilterAssignmentType('project')}
                >
                  <ToggleText>프로젝트 과제</ToggleText>
                  <ToggleTextShort>프로젝트</ToggleTextShort>
                </ToggleOption>
                <ToggleOption
                  $active={filterAssignmentType === 'routine'}
                  onClick={() => setFilterAssignmentType('routine')}
                >
                  <ToggleText>루틴 과제</ToggleText>
                  <ToggleTextShort>루틴</ToggleTextShort>
                </ToggleOption>
              </ToggleContainer>
            </ToggleSection>
            
            <FilterSection>
              <ToggleButtonSection>
                {filterAssignmentType === 'assignment' && (
                  <FilterSelectContainer>
                    <FilterSelectButton 
                      onClick={() => setIsCategoryDropdownOpen(!isCategoryDropdownOpen)}
                    >
                        <FilterOption>
                          <ColorDot $color={filterCategoryId === 'all' ? 'var(--color-border)' : 
                            categories.find(cat => cat.id === filterCategoryId)?.color || 'var(--color-border)'} />
                          <span>
                            {filterType === 'all' ? '전체 과제' : filterType === 'incomplete' ? '미완료 과제' : ''}
                            {filterCategoryId === 'all' ? (filterType === 'all' || filterType === 'incomplete' ? ' · 모든 카테고리' : '모든 카테고리') : 
                             ' · ' + (categories.find(cat => cat.id === filterCategoryId)?.name || '카테고리 선택')}
                          </span>
                        </FilterOption>
                      </FilterSelectButton>
                      
                      {isCategoryDropdownOpen && (
                        <>
                          <FilterDropdownOverlay onClick={() => setIsCategoryDropdownOpen(false)} />
                          <FilterDropdown $isOpen={isCategoryDropdownOpen}>
                            <FilterOption 
                              onClick={(e) => {
                                e.stopPropagation();
                                setFilterType('all');
                                setIsCategoryDropdownOpen(false);
                              }}
                            >
                              <span>전체 과제</span>
                            </FilterOption>
                            <FilterOption 
                              onClick={(e) => {
                                e.stopPropagation();
                                setFilterType('incomplete');
                                setIsCategoryDropdownOpen(false);
                              }}
                            >
                              <span>미완료 과제</span>
                            </FilterOption>
                            <FilterDivider />
                            <FilterOption 
                              onClick={(e) => {
                                e.stopPropagation();
                                setFilterCategoryId('all');
                                setIsCategoryDropdownOpen(false);
                              }}
                            >
                              <ColorDot $color="var(--color-border)" />
                              <span>모든 카테고리</span>
                            </FilterOption>
                            {categories.map(category => (
                              <FilterOption 
                                key={category.id}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setFilterCategoryId(category.id);
                                  setIsCategoryDropdownOpen(false);
                                }}
                              >
                                <ColorDot $color={category.color} />
                                <span>{category.name}</span>
                              </FilterOption>
                            ))}
                          </FilterDropdown>
                        </>
                      )}
                    </FilterSelectContainer>
                )}
                {filterAssignmentType === 'project' && (
                  <FilterSelectContainer>
                    <FilterSelectButton 
                      onClick={() => {
                        console.log('프로젝트 드롭다운 클릭, 현재 프로젝트들:', extractedProjects);
                        setIsProjectDropdownOpen(!isProjectDropdownOpen);
                      }}
                    >
                      <FilterOption>
                        <ColorDot $color={filterProjectId === 'all' ? 'var(--color-border)' : 
                          extractedProjects.find(proj => proj.id === filterProjectId)?.color || '#4a90e2'} />
                        <span>
                          {filterType === 'all' ? '전체 과제' : filterType === 'incomplete' ? '미완료 과제' : ''}
                          {filterProjectId === 'all' ? (filterType === 'all' || filterType === 'incomplete' ? ' · 모든 프로젝트' : '모든 프로젝트 과제') : 
                           ' · ' + (extractedProjects.find(proj => proj.id === filterProjectId)?.title || '프로젝트 선택')}
                        </span>
                      </FilterOption>
                    </FilterSelectButton>
                    
                    {isProjectDropdownOpen && (
                      <>
                        <FilterDropdownOverlay onClick={() => setIsProjectDropdownOpen(false)} />
                        <ProjectFilterDropdown $isOpen={isProjectDropdownOpen}>
                          <FilterOption 
                            onClick={(e) => {
                              e.stopPropagation();
                              setFilterType('all');
                              setIsProjectDropdownOpen(false);
                            }}
                          >
                            <span>전체 과제</span>
                          </FilterOption>
                          <FilterOption 
                            onClick={(e) => {
                              e.stopPropagation();
                              setFilterType('incomplete');
                              setIsProjectDropdownOpen(false);
                            }}
                          >
                            <span>미완료 과제</span>
                          </FilterOption>
                          <FilterDivider />
                          <FilterOption 
                            onClick={(e) => {
                              e.stopPropagation();
                              setFilterProjectId('all');
                              setIsProjectDropdownOpen(false);
                            }}
                          >
                            <ColorDot $color="var(--color-border)" />
                            <span>모든 프로젝트</span>
                          </FilterOption>
                          {extractedProjects.slice(0, 8).map(project => (
                            <FilterOption 
                              key={project.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                setFilterProjectId(project.id);
                                setIsProjectDropdownOpen(false);
                              }}
                            >
                              <ColorDot $color={project.color} />
                              <span>{project.title}</span>
                            </FilterOption>
                          ))}
                          {extractedProjects.length > 8 && (
                            <div style={{ 
                              maxHeight: '200px', 
                              overflowY: 'auto',
                              borderTop: '1px solid var(--color-border)',
                              paddingTop: '8px'
                            }}>
                              {extractedProjects.slice(8).map(project => (
                                <FilterOption 
                                  key={project.id}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setFilterProjectId(project.id);
                                    setIsProjectDropdownOpen(false);
                                  }}
                                >
                                  <ColorDot $color={project.color} />
                                  <span>{project.title}</span>
                                </FilterOption>
                              ))}
                            </div>
                          )}
                        </ProjectFilterDropdown>
                      </>
                    )}
                  </FilterSelectContainer>
                )}

                {filterAssignmentType === 'routine' && (
                  <FilterSelectContainer ref={routineDateDropdownRef}>
                    <FilterSelectButton 
                      onClick={() => setIsRoutineDateDropdownOpen(!isRoutineDateDropdownOpen)}
                    >
                      <FilterOption>
                        <ColorDot 
                          $color={getRoutineAssignmentColor()} 
                          $isToday={(() => {
                            const today = new Date();
                            return selectedRoutineDate.toDateString() === today.toDateString();
                          })()}
                        />
                        <span>
                          {filterType === 'all' ? '전체 과제' : filterType === 'incomplete' ? '미완료 과제' : ''}
                          {(() => {
                            const today = new Date();
                            const isToday = selectedRoutineDate.toDateString() === today.toDateString();
                            
                            if (filterType === 'all' || filterType === 'incomplete') {
                              if (isToday) {
                                return ' · 오늘';
                              } else {
                                const month = selectedRoutineDate.getMonth() + 1;
                                const day = selectedRoutineDate.getDate();
                                const weekday = selectedRoutineDate.toLocaleDateString('ko-KR', { weekday: 'short' });
                                return ` · ${month}월 ${day}일 (${weekday})`;
                              }
                            } else {
                              if (isToday) {
                                return '오늘';
                              } else {
                                const month = selectedRoutineDate.getMonth() + 1;
                                const day = selectedRoutineDate.getDate();
                                const weekday = selectedRoutineDate.toLocaleDateString('ko-KR', { weekday: 'short' });
                                return `${month}월 ${day}일 (${weekday})`;
                              }
                            }
                          })()}
                        </span>
                      </FilterOption>
                    </FilterSelectButton>
                    
                    {isRoutineDateDropdownOpen && (
                      <>
                        <FilterDropdownOverlay onClick={() => setIsRoutineDateDropdownOpen(false)} />
                        <FilterDropdown $isOpen={isRoutineDateDropdownOpen}>
                          <FilterOption 
                            onClick={(e) => {
                              e.stopPropagation();
                              setFilterType('all');
                              setIsRoutineDateDropdownOpen(false);
                            }}
                          >
                            <span>전체 과제</span>
                          </FilterOption>
                          <FilterOption 
                            onClick={(e) => {
                              e.stopPropagation();
                              setFilterType('incomplete');
                              setIsRoutineDateDropdownOpen(false);
                            }}
                          >
                            <span>미완료 과제</span>
                          </FilterOption>
                          <FilterDivider />
                          {getRecentDates().map((dateInfo, index) => (
                            <FilterOption
                              key={dateInfo.dateId}
                              onClick={() => {
                                setSelectedRoutineDate(dateInfo.date);
                                setIsRoutineDateDropdownOpen(false);
                              }}
                              style={{
                                backgroundColor: selectedRoutineDate.toDateString() === dateInfo.date.toDateString() ? 'var(--color-panel)' : 'var(--color-surface)',
                                fontWeight: dateInfo.isToday ? 'bold' : 'normal'
                              }}
                            >
                              <ColorDot $color={getRoutineAssignmentColor()} $isToday={dateInfo.isToday} />
                              <span>
                                {dateInfo.isToday ? '오늘' : `${dateInfo.date.getMonth() + 1}월 ${dateInfo.date.getDate()}일 (${dateInfo.dayOfWeek})`}
                              </span>
                            </FilterOption>
                          ))}
                        </FilterDropdown>
                      </>
                    )}
                  </FilterSelectContainer>
                )}

                <ActionButtonsContainer $isAllIncomplete={filterAssignmentType === 'allIncomplete'}>
                  <ZoomToggleButton onClick={() => setIsZoomed(!isZoomed)}>
                    {isZoomed ? (
                      <img src="/icons/zoom-out.svg" alt="줌 아웃" width="20" height="20" />
                    ) : (
                      <img src="/icons/zoom-in.svg" alt="줌 인" width="20" height="20" />
                    )}
                  </ZoomToggleButton>
                  {filterAssignmentType !== 'allIncomplete' && filterAssignmentType !== 'assignment' && filterAssignmentType !== 'project' && filterAssignmentType !== 'routine' && (
                    <>
                      <FilterButton
                        $active={filterType === 'all'}
                        onClick={() => setFilterType('all')}
                      >
                        <FilterButtonText>전체 과제</FilterButtonText>
                        <FilterButtonTextShort>전체</FilterButtonTextShort>
                      </FilterButton>
                      <FilterButton
                        $active={filterType === 'incomplete'}
                        onClick={() => setFilterType('incomplete')}
                      >
                        <FilterButtonText>미완료 과제</FilterButtonText>
                        <FilterButtonTextShort>미완료</FilterButtonTextShort>
                      </FilterButton>
                    </>
                  )}
                  <SaveButton
                    disabled={!hasChanges}
                    onClick={handleSave}
                  >
                    저장하기
                  </SaveButton>
                </ActionButtonsContainer>
              </ToggleButtonSection>
            </FilterSection>
          </TopSection>
          {embeddedInModal ? (
            <ModalMessageFill>
              <EmptyText>
                {filterAssignmentType === 'allIncomplete'
                  ? '미완료 과제가 없습니다.'
                  : filterAssignmentType === 'routine'
                  ? `해당 날짜에 진행하는 루틴 과제가 없습니다.`
                  : isCategoryFiltered && selectedCategory 
                    ? `"${selectedCategory.name}" 카테고리의 과제가 없습니다.`
                    : isProjectFiltered && selectedProject
                      ? `"${selectedProject.title}" 프로젝트의 과제가 없습니다.`
                      : filterType === 'incomplete' 
                        ? '미완료 과제가 없습니다.' 
                        : '과제가 없습니다.'}
              </EmptyText>
            </ModalMessageFill>
          ) : (
            <EmptyText>
              {filterAssignmentType === 'allIncomplete'
                ? '미완료 과제가 없습니다.'
                : filterAssignmentType === 'routine'
                ? `해당 날짜에 진행하는 루틴 과제가 없습니다.`
                : isCategoryFiltered && selectedCategory 
                  ? `"${selectedCategory.name}" 카테고리의 과제가 없습니다.`
                  : isProjectFiltered && selectedProject
                    ? `"${selectedProject.title}" 프로젝트의 과제가 없습니다.`
                    : filterType === 'incomplete' 
                      ? '미완료 과제가 없습니다.' 
                      : '과제가 없습니다.'}
            </EmptyText>
          )}
        </Content>
      </Container>
    );
  }

  return (
    <Container $embeddedInModal={embeddedInModal}>
      <Header $embeddedInModal={embeddedInModal}>
        {!embeddedInModal && (
          <BackButton onClick={handleBack}>
            <svg 
              width="20" 
              height="20" 
              viewBox="0 0 24 24" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
            >
              <path 
                d="M15 18L9 12L15 6" 
                stroke="currentColor" 
                strokeWidth="3" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
            </svg>
          </BackButton>
        )}
        <Title $embeddedInModal={embeddedInModal}>과제 관리 테이블</Title>
        {embeddedInModal ? (
          <ModalCloseButton onClick={onBack} aria-label="모달 닫기">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </ModalCloseButton>
        ) : (
          <div style={{ width: '32px' }} />
        )}
      </Header>
      
      <Content $embeddedInModal={embeddedInModal}>
        <TopSection>
          <ToggleSection>
            <ToggleContainer $tabCount={4}>
              <ToggleSlider $position={
                filterAssignmentType === 'allIncomplete' ? 0 :
                filterAssignmentType === 'assignment' ? 1 : 
                filterAssignmentType === 'project' ? 2 : 3
              } $tabCount={4} />
              <ToggleOption
                $active={filterAssignmentType === 'allIncomplete'}
                onClick={() => setFilterAssignmentType('allIncomplete')}
              >
                <ToggleText>미완료 전체</ToggleText>
                <ToggleTextShort>미완료</ToggleTextShort>
              </ToggleOption>
              <ToggleOption
                $active={filterAssignmentType === 'assignment'}
                onClick={() => setFilterAssignmentType('assignment')}
              >
                <ToggleText>일반 과제</ToggleText>
                <ToggleTextShort>일반</ToggleTextShort>
              </ToggleOption>
              <ToggleOption
                $active={filterAssignmentType === 'project'}
                onClick={() => setFilterAssignmentType('project')}
              >
                <ToggleText>프로젝트 과제</ToggleText>
                <ToggleTextShort>프로젝트</ToggleTextShort>
              </ToggleOption>
              <ToggleOption
                $active={filterAssignmentType === 'routine'}
                onClick={() => setFilterAssignmentType('routine')}
              >
                <ToggleText>루틴 과제</ToggleText>
                <ToggleTextShort>루틴</ToggleTextShort>
              </ToggleOption>
            </ToggleContainer>
          </ToggleSection>
          
          <FilterSection>
            <ToggleButtonSection>
              {filterAssignmentType === 'assignment' && (
                <FilterSelectContainer>
                  <FilterSelectButton 
                    onClick={() => setIsCategoryDropdownOpen(!isCategoryDropdownOpen)}
                  >
                      <FilterOption>
                        <ColorDot $color={filterCategoryId === 'all' ? 'var(--color-border)' : 
                          categories.find(cat => cat.id === filterCategoryId)?.color || 'var(--color-border)'} />
                        <span>
                          {filterType === 'all' ? '전체 과제' : filterType === 'incomplete' ? '미완료 과제' : ''}
                          {filterCategoryId === 'all' ? (filterType === 'all' || filterType === 'incomplete' ? ' · 모든 카테고리' : '모든 카테고리') : 
                           ' · ' + (categories.find(cat => cat.id === filterCategoryId)?.name || '카테고리 선택')}
                        </span>
                      </FilterOption>
                    </FilterSelectButton>
                    
                    {isCategoryDropdownOpen && (
                      <>
                        <FilterDropdownOverlay onClick={() => setIsCategoryDropdownOpen(false)} />
                        <FilterDropdown $isOpen={isCategoryDropdownOpen}>
                          <FilterOption 
                            onClick={(e) => {
                              e.stopPropagation();
                              setFilterType('all');
                              setIsCategoryDropdownOpen(false);
                            }}
                          >
                            <span>전체 과제</span>
                          </FilterOption>
                          <FilterOption 
                            onClick={(e) => {
                              e.stopPropagation();
                              setFilterType('incomplete');
                              setIsCategoryDropdownOpen(false);
                            }}
                          >
                            <span>미완료 과제</span>
                          </FilterOption>
                          <FilterDivider />
                          <FilterOption 
                            onClick={(e) => {
                              e.stopPropagation();
                              setFilterCategoryId('all');
                              setIsCategoryDropdownOpen(false);
                            }}
                          >
                            <ColorDot $color="var(--color-border)" />
                            <span>모든 카테고리</span>
                          </FilterOption>
                          {categories.map(category => (
                            <FilterOption 
                              key={category.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                setFilterCategoryId(category.id);
                                setIsCategoryDropdownOpen(false);
                              }}
                            >
                              <ColorDot $color={category.color} />
                              <span>{category.name}</span>
                            </FilterOption>
                          ))}
                        </FilterDropdown>
                      </>
                    )}
                  </FilterSelectContainer>
              )}

              {filterAssignmentType === 'project' && (
                <FilterSelectContainer>
                  <FilterSelectButton 
                    onClick={() => {
                      console.log('프로젝트 드롭다운 클릭, 현재 프로젝트들:', extractedProjects);
                      setIsProjectDropdownOpen(!isProjectDropdownOpen);
                    }}
                  >
                    <FilterOption>
                      <ColorDot $color={filterProjectId === 'all' ? 'var(--color-border)' : 
                        extractedProjects.find(proj => proj.id === filterProjectId)?.color || '#4a90e2'} />
                      <span>
                        {filterType === 'all' ? '전체 과제' : filterType === 'incomplete' ? '미완료 과제' : ''}
                        {filterProjectId === 'all' ? (filterType === 'all' || filterType === 'incomplete' ? ' · 모든 프로젝트' : '모든 프로젝트') : 
                         ' · ' + (extractedProjects.find(proj => proj.id === filterProjectId)?.title || '프로젝트 선택')}
                      </span>
                    </FilterOption>
                  </FilterSelectButton>
                  
                  {isProjectDropdownOpen && (
                    <>
                      <FilterDropdownOverlay onClick={() => setIsProjectDropdownOpen(false)} />
                      <ProjectFilterDropdown $isOpen={isProjectDropdownOpen}>
                        <FilterOption 
                          onClick={(e) => {
                            e.stopPropagation();
                            setFilterType('all');
                            setIsProjectDropdownOpen(false);
                          }}
                        >
                          <span>전체 과제</span>
                        </FilterOption>
                        <FilterOption 
                          onClick={(e) => {
                            e.stopPropagation();
                            setFilterType('incomplete');
                            setIsProjectDropdownOpen(false);
                          }}
                        >
                          <span>미완료 과제</span>
                        </FilterOption>
                        <FilterDivider />
                        <FilterOption 
                          onClick={(e) => {
                            e.stopPropagation();
                            setFilterProjectId('all');
                            setIsProjectDropdownOpen(false);
                          }}
                        >
                          <ColorDot $color="var(--color-border)" />
                          <span>모든 프로젝트</span>
                        </FilterOption>
                        {extractedProjects.slice(0, 8).map(project => (
                          <FilterOption 
                            key={project.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              setFilterProjectId(project.id);
                              setIsProjectDropdownOpen(false);
                            }}
                          >
                            <ColorDot $color={project.color} />
                            <span>{project.title}</span>
                          </FilterOption>
                        ))}
                        {extractedProjects.length > 8 && (
                          <div style={{ 
                            maxHeight: '200px', 
                            overflowY: 'auto',
                            borderTop: '1px solid var(--color-border)',
                            paddingTop: '8px'
                          }}>
                            {extractedProjects.slice(8).map(project => (
                              <FilterOption 
                                key={project.id}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setFilterProjectId(project.id);
                                  setIsProjectDropdownOpen(false);
                                }}
                              >
                                <ColorDot $color={project.color} />
                                <span>{project.title}</span>
                              </FilterOption>
                            ))}
                          </div>
                        )}
                      </ProjectFilterDropdown>
                    </>
                  )}
                </FilterSelectContainer>
              )}

              {filterAssignmentType === 'routine' && (
                <FilterSelectContainer ref={routineDateDropdownRef}>
                  <FilterSelectButton 
                    onClick={() => setIsRoutineDateDropdownOpen(!isRoutineDateDropdownOpen)}
                  >
                    <FilterOption>
                      <ColorDot 
                        $color={getRoutineAssignmentColor()} 
                        $isToday={(() => {
                          const today = new Date();
                          return selectedRoutineDate.toDateString() === today.toDateString();
                        })()}
                      />
                      <span>
                        {filterType === 'all' ? '전체 과제' : filterType === 'incomplete' ? '미완료 과제' : ''}
                        {(() => {
                          const today = new Date();
                          const isToday = selectedRoutineDate.toDateString() === today.toDateString();
                          
                          if (filterType === 'all' || filterType === 'incomplete') {
                            if (isToday) {
                              return ' · 오늘';
                            } else {
                              const month = selectedRoutineDate.getMonth() + 1;
                              const day = selectedRoutineDate.getDate();
                              const weekday = selectedRoutineDate.toLocaleDateString('ko-KR', { weekday: 'short' });
                              return ` · ${month}월 ${day}일 (${weekday})`;
                            }
                          } else {
                            if (isToday) {
                              return '오늘';
                            } else {
                              const month = selectedRoutineDate.getMonth() + 1;
                              const day = selectedRoutineDate.getDate();
                              const weekday = selectedRoutineDate.toLocaleDateString('ko-KR', { weekday: 'short' });
                              return `${month}월 ${day}일 (${weekday})`;
                            }
                          }
                        })()}
                      </span>
                    </FilterOption>
                  </FilterSelectButton>
                  
                  {isRoutineDateDropdownOpen && (
                    <>
                      <FilterDropdownOverlay onClick={() => setIsRoutineDateDropdownOpen(false)} />
                      <FilterDropdown $isOpen={isRoutineDateDropdownOpen}>
                        <FilterOption 
                          onClick={(e) => {
                            e.stopPropagation();
                            setFilterType('all');
                            setIsRoutineDateDropdownOpen(false);
                          }}
                        >
                          <span>전체 과제</span>
                        </FilterOption>
                        <FilterOption 
                          onClick={(e) => {
                            e.stopPropagation();
                            setFilterType('incomplete');
                            setIsRoutineDateDropdownOpen(false);
                          }}
                        >
                          <span>미완료 과제</span>
                        </FilterOption>
                        <FilterDivider />
                        {getRecentDates().map((dateInfo, index) => (
                          <FilterOption
                            key={dateInfo.dateId}
                            onClick={() => {
                              setSelectedRoutineDate(dateInfo.date);
                              setIsRoutineDateDropdownOpen(false);
                            }}
                            style={{
                              backgroundColor: selectedRoutineDate.toDateString() === dateInfo.date.toDateString() ? 'var(--color-panel)' : 'var(--color-surface)',
                              fontWeight: dateInfo.isToday ? 'bold' : 'normal'
                            }}
                          >
                            <ColorDot $color={getRoutineAssignmentColor()} $isToday={dateInfo.isToday} />
                            <span>
                              {dateInfo.isToday ? '오늘' : `${dateInfo.date.getMonth() + 1}월 ${dateInfo.date.getDate()}일 (${dateInfo.dayOfWeek})`}
                            </span>
                          </FilterOption>
                        ))}
                      </FilterDropdown>
                    </>
                  )}
                </FilterSelectContainer>
              )}

              <ActionButtonsContainer $isAllIncomplete={filterAssignmentType === 'allIncomplete'}>
                <ZoomToggleButton onClick={() => setIsZoomed(!isZoomed)}>
                  {isZoomed ? (
                    <img src="/icons/zoom-out.svg" alt="줌 아웃" width="20" height="20" />
                  ) : (
                    <img src="/icons/zoom-in.svg" alt="줌 인" width="20" height="20" />
                  )}
                </ZoomToggleButton>
                {filterAssignmentType !== 'allIncomplete' && filterAssignmentType !== 'assignment' && filterAssignmentType !== 'project' && filterAssignmentType !== 'routine' && (
                  <>
                    <FilterButton
                      $active={filterType === 'all'}
                      onClick={() => setFilterType('all')}
                    >
                      <FilterButtonText>전체 과제</FilterButtonText>
                      <FilterButtonTextShort>전체</FilterButtonTextShort>
                    </FilterButton>
                    <FilterButton
                      $active={filterType === 'incomplete'}
                      onClick={() => setFilterType('incomplete')}
                    >
                      <FilterButtonText>미완료 과제</FilterButtonText>
                      <FilterButtonTextShort>미완료</FilterButtonTextShort>
                    </FilterButton>
                  </>
                )}
                <SaveButton
                  disabled={!hasChanges}
                  onClick={handleSave}
                >
                  저장하기
                </SaveButton>
              </ActionButtonsContainer>
            </ToggleButtonSection>

          </FilterSection>
        </TopSection>

        {/* 상태 변경 버튼 섹션 */}
        <StatusChangeSection>
          <StatusChangeButton
            $isSelected={false}
            $disabled={selectedCells.size === 0}
            onClick={() => changeSelectedCellsStatus('pending')}
          >
            <StatusChangeButtonColorDot $status="pending">1</StatusChangeButtonColorDot>
            <StatusChangeButtonText $isSelected={false}>
              미확인
            </StatusChangeButtonText>
          </StatusChangeButton>
          <StatusChangeButton
            $isSelected={false}
            $disabled={selectedCells.size === 0}
            onClick={() => changeSelectedCellsStatus('confirmed')}
          >
            <StatusChangeButtonColorDot $status="confirmed">2</StatusChangeButtonColorDot>
            <StatusChangeButtonText $isSelected={false}>
              확인
            </StatusChangeButtonText>
          </StatusChangeButton>
          <StatusChangeButton
            $isSelected={false}
            $disabled={selectedCells.size === 0}
            onClick={() => changeSelectedCellsStatus('delayed')}
          >
            <StatusChangeButtonColorDot $status="delayed">3</StatusChangeButtonColorDot>
            <StatusChangeButtonText $isSelected={false}>
              지연확인
            </StatusChangeButtonText>
          </StatusChangeButton>
          <StatusChangeButton
            $isSelected={false}
            $disabled={selectedCells.size === 0}
            onClick={() => changeSelectedCellsStatus('exempted')}
          >
            <StatusChangeButtonColorDot $status="exempted">4</StatusChangeButtonColorDot>
            <StatusChangeButtonText $isSelected={false}>
              제외
            </StatusChangeButtonText>
          </StatusChangeButton>
        </StatusChangeSection>


        <TableContainer $embeddedInModal={embeddedInModal}>
          {/* 스크롤 가능한 내용 */}
          <ScrollableContent $embeddedInModal={embeddedInModal}>
            <TableGridWrapper>
            {/* 통합 헤더 그리드 */}
            <UnifiedHeaderGrid 
              $columnCount={2 + regular.length + project.length + routine.length}
              $embeddedInModal={embeddedInModal}
            >
              {/* 번호 헤더 */}
              <NumberHeaderCell>번호</NumberHeaderCell>
              
              {/* 이름 헤더 */}
              <NameHeaderCell>이름</NameHeaderCell>
              
              {/* 마감일 헤더 */}
              {regular.map((assignment, index) => (
                <DueDateCell 
                  key={`date-${assignment.id}`}
                  color={assignment.categoryId ? 
                    (categories.find(cat => cat.id === assignment.categoryId)?.color || '#a2a2a2') : 
                    '#a2a2a2'
                  }
                  style={{ 
                    gridColumn: `${3 + index}`,
                    gridRow: '1'
                  }}
                >
                  {assignment.dueDate ? (() => {
                    try {
                      const date = new Date(assignment.dueDate);
                      if (isNaN(date.getTime())) return '마감일 없음';
                      const month = date.getMonth() + 1;
                      const day = date.getDate();
                      const weekday = date.toLocaleDateString('ko-KR', { weekday: 'short' });
                      return (
                        <>
                          <div>{month}/{day}</div>
                          <div>({weekday})</div>
                        </>
                      );
                    } catch (error) {
                      console.error('날짜 변환 오류:', error);
                      return '마감일 없음';
                    }
                  })() : '마감일 없음'}
                </DueDateCell>
              ))}
              {project.map((assignment, index) => (
                <DueDateCell 
                  key={`date-${assignment.id}-${assignment.subTaskId}`}
                  color={assignment.color || getProjectColor(assignment.projectTitle || '')}
                  style={{ 
                    gridColumn: `${3 + regular.length + index}`,
                    gridRow: '1'
                  }}
                >
                  {assignment.dueDate ? (() => {
                    try {
                      const date = new Date(assignment.dueDate);
                      if (isNaN(date.getTime())) return '마감일 없음';
                      const month = date.getMonth() + 1;
                      const day = date.getDate();
                      const weekday = date.toLocaleDateString('ko-KR', { weekday: 'short' });
                      return (
                        <>
                          <div>{month}/{day}</div>
                          <div>({weekday})</div>
                        </>
                      );
                    } catch (error) {
                      console.error('날짜 변환 오류:', error);
                      return '마감일 없음';
                    }
                  })() : '마감일 없음'}
                </DueDateCell>
              ))}
              {routine.map((assignment, index) => (
                <DueDateCell 
                  key={`date-${assignment.id}`}
                  color={assignment.color || '#a2a2a2'}
                  style={{ 
                    gridColumn: `${3 + regular.length + project.length + index}`,
                    gridRow: '1'
                  }}
                >
                  {(() => {
                    try {
                      // 루틴 과제의 경우 선택된 날짜(오늘 날짜)를 표시
                      const date = selectedRoutineDate;
                      if (isNaN(date.getTime())) return '날짜 없음';
                      const month = date.getMonth() + 1;
                      const day = date.getDate();
                      const weekday = date.toLocaleDateString('ko-KR', { weekday: 'short' });
                      return (
                        <>
                          <div>{month}/{day}</div>
                          <div>({weekday})</div>
                        </>
                      );
                    } catch (error) {
                      console.error('날짜 변환 오류:', error);
                      return '날짜 없음';
                    }
                  })()}
                </DueDateCell>
              ))}
              
              {/* 과제 제목 헤더 */}
              {regular.map((assignment, index) => (
                <GridHeaderCell 
                  key={assignment.id}
                  style={{ 
                    gridColumn: `${3 + index}`,
                    gridRow: '2',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    writingMode: 'horizontal-tb',
                    textOrientation: 'initial',
                    background: 'var(--color-surface)',
                    wordBreak: 'break-word',
                    overflow: 'hidden',
                    lineHeight: '1.2',
                    maxHeight: '100%',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease'
                  }}
                  onClick={() => handleAssignmentTitleClick(assignment.id, 'assignment')}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--color-panel)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--color-surface)';
                  }}
                >
                  <div style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxHeight: '100%',
                    width: '100%',
                    wordBreak: 'break-word',
                    whiteSpace: 'normal',
                    lineHeight: '1.2'
                  }}>
                    {assignment.title}
                  </div>
                </GridHeaderCell>
              ))}
              {project.map((assignment, index) => (
                <GridHeaderCell 
                  key={`${assignment.id}-${assignment.subTaskId}`}
                  style={{ 
                    gridColumn: `${3 + regular.length + index}`,
                    gridRow: '2',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    writingMode: 'horizontal-tb',
                    textOrientation: 'initial',
                    background: 'var(--color-surface)',
                    wordBreak: 'break-word',
                    overflow: 'hidden',
                    lineHeight: '1.2',
                    maxHeight: '100%',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease'
                  }}
                  onClick={() => handleAssignmentTitleClick(assignment.id, 'project', assignment.subTaskId)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--color-panel)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--color-surface)';
                  }}
                >
                  <div style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxHeight: '100%',
                    width: '100%',
                    wordBreak: 'break-word',
                    whiteSpace: 'normal',
                    lineHeight: '1.2'
                  }}>
                    {assignment.projectTitle && assignment.subTaskTitle ? (
                      <>
                        <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '2px' }}>
                          {assignment.projectTitle}
                        </div>
                        <div style={{ fontSize: '11px', color: '#6c757d' }}>
                          {assignment.subTaskTitle}
                        </div>
                      </>
                    ) : (
                      assignment.title
                    )}
                  </div>
                </GridHeaderCell>
              ))}
              {routine.map((assignment, index) => (
                <GridHeaderCell 
                  key={assignment.id}
                  style={{ 
                    gridColumn: `${3 + regular.length + project.length + index}`,
                    gridRow: '2',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    writingMode: 'horizontal-tb',
                    textOrientation: 'initial',
                    background: 'var(--color-surface)',
                    wordBreak: 'break-word',
                    overflow: 'hidden',
                    lineHeight: '1.2',
                    maxHeight: '100%',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease'
                  }}
                  onClick={() => handleAssignmentTitleClick(assignment.id, 'routine')}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--color-panel)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--color-surface)';
                  }}
                >
                  <div style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxHeight: '100%',
                    width: '100%',
                    wordBreak: 'break-word',
                    whiteSpace: 'normal',
                    lineHeight: '1.2'
                  }}>
                    {assignment.routineTitle || assignment.title}
                  </div>
                </GridHeaderCell>
              ))}
            </UnifiedHeaderGrid>

            {/* 통합 데이터 그리드 */}
            <UnifiedDataGrid 
              $columnCount={2 + regular.length + project.length + routine.length}
              $rowCount={tableData.studentStatuses.length}
              $isZoomed={isZoomed}
            >
              {tableData.studentStatuses.map((student, studentIndex) => (
                <React.Fragment key={`student-${student.studentId}`}>
                  {/* 번호 셀 */}
                  <GridDataCell 
                    key={`number-${student.studentId}`}
                    $isZoomed={isZoomed}
                    style={{ 
                      gridColumn: '1',
                      gridRow: `${studentIndex + 1}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'sticky',
                      left: 0,
                      zIndex: 5,
                      background: 'var(--color-surface)'
                    }}
                  >
                    {student.studentNumber ? `${student.studentNumber}번` : `${studentIndex + 1}번`}
                  </GridDataCell>
                  
                  {/* 이름 셀 */}
                  <GridDataCell 
                    key={`name-${student.studentId}`}
                    $isZoomed={isZoomed}
                    style={{ 
                      gridColumn: '2',
                      gridRow: `${studentIndex + 1}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'sticky',
                      left: '40px',
                      zIndex: 5,
                      background: 'var(--color-surface)'
                    }}
                  >
                    {student.studentName}
                  </GridDataCell>
                  
                  {/* 과제 상태 셀들 */}
                  {regular.map((assignment, assignmentIndex) => {
                    const assignmentStatus = student.assignmentStatuses[assignment.id];
                    const status = assignmentStatus?.status || 'pending';
                    const isAssigned = assignmentStatus?.isAssigned !== false;
                    return (
                      <GridDataCell 
                        key={`${student.studentId}-${assignment.id}`}
                        $isZoomed={isZoomed}
                        style={{ 
                          gridColumn: `${3 + assignmentIndex}`,
                          gridRow: `${studentIndex + 1}`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <StatusCell 
                          $status={(() => {
                            const changeData = pendingChanges.get(`${student.studentId}-${assignment.id}`);
                            return changeData?.status || status;
                          })()}
                          $isAssigned={isAssigned}
                          $isZoomed={isZoomed}
                          $isSelected={selectedCells.has(`${student.studentId}-${assignment.id}`)}
                          $hasPendingChange={pendingChanges.has(`${student.studentId}-${assignment.id}`)}
                          onClick={(e) => handleCellClick(student.studentId, assignment.id, e)}
                          onDoubleClick={() => handleCellDoubleClick(student.studentId, assignment.id, 'assignment')}
                        >
                          {isAssigned && (
                            <StatusIcon $status={(() => {
                              const changeData = pendingChanges.get(`${student.studentId}-${assignment.id}`);
                              return changeData?.status || status;
                            })()} $isAssigned={isAssigned}>
                              {getStatusIcon((() => {
                                const changeData = pendingChanges.get(`${student.studentId}-${assignment.id}`);
                                return changeData?.status || status;
                              })())}
                            </StatusIcon>
                          )}
                        </StatusCell>
                      </GridDataCell>
                    );
                  })}
                  {project.map((assignment, assignmentIndex) => {
                    const uniqueAssignmentId = `${assignment.id}-${assignment.subTaskId}`; // V4 서비스와 동일한 키 형식
                    const keyValue = `${student.studentId}-${uniqueAssignmentId}`;
                    const assignmentStatus = student.assignmentStatuses[uniqueAssignmentId];
                    
                    // 디버깅: 첫 번째 멤버의 첫 번째 프로젝트 과제만 로그 출력
                    if (studentIndex === 0 && assignmentIndex === 0) {
                      console.log('🔍 프로젝트 과제 셀 디버깅:', {
                        assignmentId: assignment.id,
                        subTaskId: assignment.subTaskId,
                        uniqueAssignmentId,
                        keyValue,
                        assignmentStatusExists: !!assignmentStatus,
                        selectedCellsHasKey: selectedCells.has(keyValue)
                      });
                    }
                    const status = assignmentStatus?.status || 'pending';
                    const isAssigned = assignmentStatus?.isAssigned !== false;
                    return (
                      <GridDataCell 
                        key={keyValue}
                        $isZoomed={isZoomed}
                        style={{ 
                          gridColumn: `${3 + regular.length + assignmentIndex}`,
                          gridRow: `${studentIndex + 1}`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <StatusCell 
                          $status={(() => {
                            const changeData = pendingChanges.get(keyValue);
                            return changeData?.status || status;
                          })()}
                          $isAssigned={isAssigned}
                          $isZoomed={isZoomed}
                          $isSelected={selectedCells.has(keyValue)}
                          $hasPendingChange={pendingChanges.has(keyValue)}
                          onClick={(e) => handleCellClick(student.studentId, uniqueAssignmentId, e)}
                          onDoubleClick={() => handleCellDoubleClick(student.studentId, assignment.id, 'project')}
                        >
                          {isAssigned && (
                            <StatusIcon $status={(() => {
                              const changeData = pendingChanges.get(keyValue);
                              return changeData?.status || status;
                            })()} $isAssigned={isAssigned}>
                              {getStatusIcon((() => {
                                const changeData = pendingChanges.get(keyValue);
                                return changeData?.status || status;
                              })())}
                            </StatusIcon>
                          )}
                        </StatusCell>
                      </GridDataCell>
                    );
                  })}
                  
                  {/* 루틴 과제 데이터 셀 */}
                  {routine.map((assignment, assignmentIndex) => {
                    const status = student.assignmentStatuses[assignment.id]?.status || 'pending';
                    const isAssigned = student.assignmentStatuses[assignment.id]?.isAssigned || false;
                    const cellKey = `${student.studentId}-${assignment.id}`;
                    
                    return (
                      <GridDataCell 
                        key={cellKey}
                        $isZoomed={isZoomed}
                        style={{ 
                          gridColumn: `${3 + regular.length + project.length + assignmentIndex}`,
                          gridRow: `${studentIndex + 1}`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <StatusCell 
                          $status={(() => {
                            const changeData = pendingChanges.get(cellKey);
                            return changeData?.status || status;
                          })()}
                          $isAssigned={isAssigned}
                          $isZoomed={isZoomed}
                          $isSelected={selectedCells.has(cellKey)}
                          $hasPendingChange={pendingChanges.has(cellKey)}
                          onClick={(e) => handleCellClick(student.studentId, assignment.id, e)}
                          onDoubleClick={() => handleCellDoubleClick(student.studentId, assignment.id, 'routine')}
                        >
                          {isAssigned && (
                            <StatusIcon $status={(() => {
                              const changeData = pendingChanges.get(cellKey);
                              return changeData?.status || status;
                            })()} $isAssigned={isAssigned}>
                              {getStatusIcon((() => {
                                const changeData = pendingChanges.get(cellKey);
                                return changeData?.status || status;
                              })())}
                            </StatusIcon>
                          )}
                        </StatusCell>
                      </GridDataCell>
                    );
                  })}
                </React.Fragment>
              ))}
            </UnifiedDataGrid>
            </TableGridWrapper>
          </ScrollableContent>
        </TableContainer>

        {/* 성능 정보 표시 (개발용 - 숨김 처리) */}
        {/* <PerformanceInfo>
          로딩 시간 {loadTime}ms | 쿼리 수 {queryCount}개 | 
          멤버 {tableData.studentStatuses.length}명 | 과제 {filteredAssignments.length}개
        </PerformanceInfo> */}

      </Content>
      
      {/* 상세 모달 */}
      {isDetailModalOpen && selectedStudentId && selectedAssignmentId && selectedAssignmentType && (() => {
        const detailAssignment = tableData?.allAssignments.find((a) => a.id === selectedAssignmentId);
        const detailModalKey = detailAssignment
          ? buildDetailCacheKey(selectedStudentId, detailAssignment, selectedAssignmentType)
          : selectedStudentId;

        return (
        <StudentDetailModal
          key={detailModalKey}
          isOpen={isDetailModalOpen}
          onClose={closeDetailModal}
          onSave={handleDetailModalSave}
          studentName={selectedStudentName}
          studentNumber={selectedStudentNumber}
          currentStatus={currentStatus}
          currentPoints={currentPoints}
          currentMemo={currentMemo}
          assignmentTitle={selectedAssignmentTitle}
          assignmentType={selectedAssignmentType}
          projectTitle={selectedAssignmentType === 'project' ? 
            tableData?.allAssignments.find(a => a.id === selectedAssignmentId)?.projectTitle : undefined}
          subTaskTitle={selectedAssignmentType === 'project' ? 
            tableData?.allAssignments.find(a => a.id === selectedAssignmentId)?.subTaskTitle : undefined}
          routineTitle={selectedAssignmentType === 'routine' ? 
            tableData?.allAssignments.find(a => a.id === selectedAssignmentId)?.routineTitle : undefined}
          dateId={selectedAssignmentType === 'routine' ? 
            tableData?.allAssignments.find(a => a.id === selectedAssignmentId)?.dateId : undefined}
            onNavigate={handleStudentNavigation}
            canNavigatePrev={currentStudentIndex > 0}
            canNavigateNext={currentStudentIndex < (tableData?.studentStatuses.length || 0) - 1}
            defaultPoints={defaultPoints}
            statusDefaultPoints={statusDefaultPoints}
            assignmentBasePoints={tableData?.allAssignments.find(a => a.id === selectedAssignmentId)?.basePoints || 0}
            submissionLink={currentSubmissionLink}
            submissionMessage={currentSubmissionMessage}
            submittedAt={currentSubmittedAt}
            submissionImagePath={currentSubmissionImagePath}
            submissionImageUpdatedAt={currentSubmissionImageUpdatedAt}
            initialIsDeleted={currentIsDeleted}
            submissionFeaturesEnabled={isStandardPlanOrAbove(user?.userType)}
        />
        );
      })()}

      {/* 일반 과제 체크 모달 */}
      {isGeneralAssignmentCheckModalOpen && selectedGeneralAssignment && (
        <AssignmentCheckModal
          isOpen={isGeneralAssignmentCheckModalOpen}
          mode="general"
          memberGroup={currentMemberGroup}
          userType={user?.userType}
          assignment={selectedGeneralAssignment}
          projectAssignment={null}
          projectSubTasks={[]}
          selectedSubTaskId=""
          routine={undefined}
          selectedDate={new Date()}
          existingStatuses={selectedGeneralAssignmentStatuses}
          members={tableData?.studentStatuses.map(student => ({
            id: student.studentId,
            studentId: student.studentId,
            name: student.studentName,
            studentNumber: student.studentNumber,
            isActive: true,
            number: student.studentNumber,
            accessCode: '',
            createdAt: new Date()
          })) || []}
          onClose={() => {
            setIsGeneralAssignmentCheckModalOpen(false);
            clearAllSelections();
          }}
          onSave={async (updatedStatuses) => {
            console.log('✅ 일반 과제 상태 업데이트:', updatedStatuses);
            
            try {
              // 서버에 저장
              if (user?.id && currentClass?.id && selectedGeneralAssignment?.id) {
                const studentUpdates = updatedStatuses.map(status => ({
                  studentId: status.studentId,
                  status: status.status,
                  earnedPoints: status.earnedPoints || 0,
                  memo: status.memo || '',
                  ...(status.submissionLink !== undefined && { submissionLink: status.submissionLink }),
                  ...(status.submissionMessage !== undefined && { submissionMessage: status.submissionMessage }),
                  ...(status.submittedAt !== undefined && { submittedAt: status.submittedAt })
                }));
                
                await assignmentService.batchUpdateStudentStatusesOptimized(
                  user.id,
                  currentClass.id,
                  selectedGeneralAssignment.id,
                  studentUpdates
                );
                console.log('✅ 서버에 일반 과제 상태 저장 완료');
              }
              
              // 캐시에 변경사항 반영
              updatedStatuses.forEach(statusUpdate => {
                const cellKey = `${statusUpdate.studentId}-${selectedGeneralAssignment.id}`;
                setPendingChanges(prev => {
                  const newMap = new Map(prev);
                  newMap.set(cellKey, {
                    status: statusUpdate.status,
                    points: statusUpdate.earnedPoints || 0,
                    memo: statusUpdate.memo || ''
                  });
                  return newMap;
                });
              });
              
              // 테이블 데이터 다시 로드하여 UI 갱신
              await loadData();
              
            } catch (error) {
              console.error('❌ 일반 과제 상태 저장 실패:', error);
              alert('저장 중 오류가 발생했습니다.');
            }
            
            setIsGeneralAssignmentCheckModalOpen(false);
            clearAllSelections();
          }}
          statusDefaultPoints={statusDefaultPoints}
          defaultPoints={defaultPoints}
          saveButtonText="확인"
        />
      )}

      {/* 프로젝트 과제 체크 모달 */}
      {isProjectAssignmentCheckModalOpen && selectedProjectAssignmentForTable && (
        <AssignmentCheckModal
          isOpen={isProjectAssignmentCheckModalOpen}
          mode="project"
          memberGroup={currentMemberGroup}
          userType={user?.userType}
          assignment={undefined}
          projectAssignment={selectedProjectAssignmentForTable}
          projectSubTasks={selectedProjectSubTasksForTable}
          selectedSubTaskId={selectedSubTaskIdForTable}
          routine={undefined}
          selectedDate={new Date()}
          existingStatuses={selectedProjectAssignmentStatuses}
          members={tableData?.studentStatuses.map(student => ({
            id: student.studentId,
            studentId: student.studentId,
            name: student.studentName,
            studentNumber: student.studentNumber,
            isActive: true,
            number: student.studentNumber,
            accessCode: '',
            createdAt: new Date()
          })) || []}
          onClose={() => {
            setIsProjectAssignmentCheckModalOpen(false);
            clearAllSelections();
          }}
          onSave={async (updatedStatuses) => {
            console.log('✅ 프로젝트 과제 상태 업데이트:', updatedStatuses);
            
            try {
              // 서버에 저장
              if (user?.id && currentClass?.id && selectedProjectAssignmentIdForTable && selectedSubTaskIdForTable) {
                const projectId = selectedProjectAssignmentIdForTable.split('-')[0];
                const studentUpdates = updatedStatuses.map(status => ({
                  studentId: status.studentId,
                  status: status.status,
                  earnedPoints: status.earnedPoints || 0,
                  memo: status.memo ?? ''
                }));
                
                await projectAssignmentService.batchUpdateSubTaskStudentStatuses(
                  user.id,
                  currentClass.id,
                  projectId,
                  selectedSubTaskIdForTable,
                  studentUpdates,
                  currentMemberGroup
                );
                console.log('✅ 서버에 프로젝트 과제 상태 저장 완료');
              }
              
              // 캐시에 변경사항 반영
              updatedStatuses.forEach(statusUpdate => {
                const uniqueAssignmentId = `${selectedProjectAssignmentIdForTable}-${selectedSubTaskIdForTable}`;
                const cellKey = `${statusUpdate.studentId}-${uniqueAssignmentId}`;
                setPendingChanges(prev => {
                  const newMap = new Map(prev);
                  newMap.set(cellKey, {
                    status: statusUpdate.status,
                    points: statusUpdate.earnedPoints || 0,
                    memo: statusUpdate.memo || ''
                  });
                  return newMap;
                });
              });
              
              // 테이블 데이터 다시 로드하여 UI 갱신
              await loadData();
              
            } catch (error) {
              console.error('❌ 프로젝트 과제 상태 저장 실패:', error);
              alert('저장 중 오류가 발생했습니다.');
            }
            
            setIsProjectAssignmentCheckModalOpen(false);
            clearAllSelections();
          }}
          statusDefaultPoints={statusDefaultPoints}
          defaultPoints={defaultPoints}
          saveButtonText="확인"
        />
      )}

      {/* 루틴 과제 체크 모달 */}
      {isRoutineAssignmentCheckModalOpen && selectedRoutineAssignmentForTable && (
        <AssignmentCheckModal
          isOpen={isRoutineAssignmentCheckModalOpen}
          mode="routine"
          memberGroup={currentMemberGroup}
          userType={user?.userType}
          assignment={undefined}
          projectAssignment={undefined}
          projectSubTasks={[]}
          selectedSubTaskId=""
          routine={selectedRoutineAssignmentForTable}
          selectedDate={selectedRoutineDateForTable}
          existingStatuses={selectedRoutineAssignmentStatuses}
          userId={user?.id}
          classId={currentClass?.id}
          members={tableData?.studentStatuses.map(student => ({
            id: student.studentId,
            studentId: student.studentId,
            name: student.studentName,
            studentNumber: student.studentNumber,
            isActive: true,
            number: student.studentNumber,
            accessCode: '',
            createdAt: new Date()
          })) || []}
          onClose={() => {
            setIsRoutineAssignmentCheckModalOpen(false);
            clearAllSelections();
          }}
          onSave={async (updatedStatuses) => {
            console.log('✅ 루틴 과제 상태 업데이트:', updatedStatuses);
            
            try {
              // 서버에 저장 (routineAssignmentService 사용 - assignmentMemoService 호출 포함)
              if (user?.id && currentClass?.id && selectedRoutineAssignmentIdForTable) {
                const dateId = dateToDateId(selectedRoutineDateForTable);
                await routineAssignmentService.batchUpdateRoutineAssignmentStatusOptimized(
                  user.id,
                  currentClass.id,
                  selectedRoutineAssignmentIdForTable,
                  dateId,
                  updatedStatuses.map(s => ({
                    studentId: s.studentId,
                    status: s.status,
                    earnedPoints: s.earnedPoints || 0,
                    memo: s.memo ?? ''
                  })),
                  currentMemberGroup,
                  {
                    fillMissingAssignedAsPending: true,
                    assignedStudentIds: selectedRoutineAssignmentForTable?.assignedStudentIds || []
                  }
                );
                console.log('✅ 서버에 루틴 과제 상태 일괄 저장 완료');
              }
              
              // 캐시에 변경사항 반영
              updatedStatuses.forEach(statusUpdate => {
                const dateId = dateToDateId(selectedRoutineDateForTable);
                const assignmentId = `${selectedRoutineAssignmentIdForTable}-${dateId}`;
                const cellKey = `${statusUpdate.studentId}-${assignmentId}`;
                
                console.log('🔍 루틴 과제 cellKey 생성:', {
                  studentId: statusUpdate.studentId,
                  routineId: selectedRoutineAssignmentIdForTable,
                  dateId,
                  assignmentId,
                  cellKey
                });
                
                setPendingChanges(prev => {
                  const newMap = new Map(prev);
                  newMap.set(cellKey, {
                    status: statusUpdate.status,
                    points: statusUpdate.earnedPoints || 0,
                    memo: statusUpdate.memo || ''
                  });
                  return newMap;
                });
              });
              
              // 테이블 데이터 다시 로드하여 UI 갱신
              await loadData();
              
            } catch (error) {
              console.error('❌ 루틴 과제 상태 저장 실패:', error);
              alert('저장 중 오류가 발생했습니다.');
            }
            
            setIsRoutineAssignmentCheckModalOpen(false);
            clearAllSelections();
          }}
          statusDefaultPoints={statusDefaultPoints}
          defaultPoints={defaultPoints}
          saveButtonText="확인"
        />
      )}
      
      <ConfirmModal
        isOpen={showWarningModal}
        onClose={handleCloseWarningOnly}
        onCancel={handleExitWithoutSave}
        onConfirm={() => {
          void handleSaveAndExit();
        }}
        title={['변경된 데이터가 있습니다.', '저장하지 않고 닫으시겠습니까?']}
        cancelText="저장하지 않고 닫기"
        confirmText="저장하고 닫기"
        confirmDisabled={!hasChanges || (pendingChanges.size === 0 && submissionChanges.size === 0)}
        icon="!"
        iconBackground="var(--status-pending)"
      />
      
      {/* 저장 호버 버튼 (화면 가로 800px 이하일 때만 표시) */}
      <SaveFloatingButton 
        $isDisabled={!hasChanges || (pendingChanges.size === 0 && submissionChanges.size === 0)}
        $embeddedInModal={embeddedInModal}
        disabled={!hasChanges || (pendingChanges.size === 0 && submissionChanges.size === 0)}
        onClick={handleSave}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M19 21H5C3.89543 21 3 20.1046 3 19V5C3 3.89543 3.89543 3 5 3H16L21 8V19C21 20.1046 20.1046 21 19 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M17 21V13H7V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M7 3V8H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </SaveFloatingButton>
    </Container>
  );
};

export default AssignmentTablePageFinal;

// 저장 호버 버튼
const SaveFloatingButton = styled.button<{ $isDisabled?: boolean; $embeddedInModal?: boolean }>`
  position: ${props => (props.$embeddedInModal ? 'absolute' : 'fixed')};
  bottom: ${props =>
    props.$embeddedInModal
      ? '16px'
      : 'calc(env(safe-area-inset-bottom, 0px) + 68px)'};
  right: ${props => (props.$embeddedInModal ? '16px' : '42px')};
  margin-left: 0;
  margin-right: 0;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: none;
  background: ${props => props.$isDisabled ? '#ccc' : '#10504A'};
  color: white;
  cursor: ${props => props.$isDisabled ? 'not-allowed' : 'pointer'};
  box-shadow: ${props => props.$isDisabled ? '0 2px 8px rgba(0, 0, 0, 0.1)' : '0 4px 16px rgba(var(--color-accent-rgb), 0.3)'};
  transition: all 0.3s ease;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${props => props.$isDisabled ? 0.6 : 1};
  
  &:hover:not(:disabled) {
    background: #0D4039;
    transform: translateY(-2px) scale(1.06);
    box-shadow: 0 8px 24px rgba(var(--color-accent-rgb), 0.45);
  }
  
  &:active:not(:disabled) {
    transform: scale(0.95);
  }
  
  /* 다크모드: 흰색 버튼에 진한 회색 아이콘 */
  :root:not([data-theme='light']) & {
    background: ${props => props.$isDisabled ? '#666' : '#ffffff'};
    border: 1px solid var(--color-border);
    color: ${props => props.$isDisabled ? '#999' : '#505050'};
    box-shadow: ${props => props.$isDisabled ? '0 2px 8px rgba(0, 0, 0, 0.1)' : '0 4px 16px rgba(0, 0, 0, 0.2)'};
  }
  
  :root:not([data-theme='light']) &:hover:not(:disabled) {
    background: var(--color-panel);
    border-color: var(--color-text-muted);
    color: #404040;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
  }
  
  :root:not([data-theme='light']) & svg {
    stroke: ${props => props.$isDisabled ? '#999' : '#505050'};
  }
  
`;
